import { create } from 'zustand'
import { opLabel } from './narrationReceipt'
import { parseRunAssessments, type RunAssessmentWorkspace } from './runAssessment'
import {
  parseComputationalWorkspace,
  type ComputationalWorkspaceState,
} from './computationalWorkspace'
import { parseExperimentWorkspace, type ExperimentWorkspaceState } from './experiments'
import type {
  Bridge,
  ConjectureAdmission,
  DeriveOp,
  FieldSweep,
  Directive,
  Filters,
  Frame,
  Lens,
  LogicalOpKind,
  ModelCallReceipt,
  ModelStamp,
  MoveReason,
  Op,
  OpKind,
  PortfolioRecord,
  ReasonNode,
  ReasonedEndingCounts,
  Run,
  RunBudget,
  RunOpen,
  RunVerb,
  RunStop,
  Session,
  Spend,
  Temper,
  UntilCondition,
  Viewport,
  Programme,
} from './types'
import { withSweep } from './sweep'
import {
  addSpendRow,
  bridgeReadable,
  bridgeTouches,
  claimMarks,
  DEFAULT_FILTERS,
  evidenceMarks,
  mergeSpend,
  OP_LABEL,
  pairKey,
  projectLabel,
  spentNothing,
  withPeak,
} from './types'
import {
  declaredAfter,
  declaredChanged,
  parseDeclared,
  serialiseDeclared,
  toggleFolded,
  withDeclared,
  withoutDeclared,
} from './projects'
import { withOpened } from './unread'
import { filtersShowing, withChannelsOn } from './visibility'
import type { ModelEntry, ProviderId, ResolvedJudge, ResolvedModel, Role } from './models'
import type { WaitCause } from '../engine/providers/contract'
import { chargeLine, passLanded } from './vocabulary'
import {
  DEFAULT_JUDGE_ID,
  JUDGE_ENDPOINT,
  DEFAULT_MODEL_ID,
  MODELS,
  PROVIDERS,
  EFFORT_WORK,
  ROLES,
  baseUrlProblem,
  estimateSpendUsd,
  estimateUsd,
  findJudge,
  findModel,
  hostOfBase,
  isEffort,
  keyCardProviders,
  priceOf,
  stampOf,
  type Effort,
  type EffortLevels,
  type EffortWork,
} from './models'
import { clampStretch, layout, type Bounds } from './layout'
import {
  centerView,
  fitField,
  fitView,
  followView,
  lookView,
  openedTheGraph,
  openingView,
  visibleField,
  LOOK_MARGIN,
  type Inset,
} from './framing'
import { uid } from './id'
import { fnv1a64 } from './similarity'
import { clamp } from './geometry'
import { DEFAULT_TEMPO, TEMPOS } from './tempo'
import {
  CAM_HOME,
  FIT_EDGE,
  camFor,
  contactHeight,
  clampCam,
  dolly,
  makeProjector,
  orbit,
  pan,
  raise,
  type Cam,
} from './space'
import { motion } from './motion'
import { pulses } from './pulse'
import { emptySession, makeNode } from './session'
import { DEMO_SESSION_ID, loadBootSession, loadPrefs, type StoredSession } from './persist'
import { nameFor } from './name'
import { MAX_BRIEF, briefForBranch, briefWrite, stampDirectives } from './brief'
import { announceRename } from './rename'
import { UP_NEXT_MAX, upNextHere, type UpNextItem } from './upNext'
import { TRAIL_KEEP } from './searchTrail'
import { coachMute, coachRetire, coachUnmute, freshCoach, type CoachState } from './coach'
import type { Weighting } from './weight'
import { latestFinishedRun, type CueKind, type CueLens } from './cues'
import {
  archiveVerificationsFor,
  mergeVerificationArchive,
  mergeVerificationEvents,
  verificationEvents,
} from './rebase'
import { writtenAt } from './rewrite'
import { mergeSurveyEvents, surveyEvents } from './survey'
import {
  createResearchState,
  normalizeResearchState,
  pruneManualEvidenceBindingsForNodes,
} from './research'
import type { ResearchState } from './research'
import { appendKnowledgeRevision, captureKnowledgeRevision } from './knowledgeHistory'
import { applySemanticSnapshot as materialiseSemanticSnapshot } from './semanticApply'
import type { SemanticActor, SemanticSnapshot } from './semanticGit'

export type Theme = 'light' | 'dark'
export type RightTab = 'node' | 'ask' | 'run' | 'breakthroughs'
/** Two modes, one sheet: the first screen, and the same controls afterwards. */
export type SetupMode = 'splash' | 'settings'
/** How autonomous reasoning moves are sent to the selected reasoning model. */
export type RunExecution = 'sequential' | 'portfolio'
/**
 * What a run does when nobody has chosen. `portfolio` since D76: two or three
 * compatible angles on one node in one request, at one shared deliberation.
 * The ONE expression of that fallback — `seededTune`, the composer's spec and
 * the loop all read it — so a stored tune that names no execution boots into
 * the same run the composer would start.
 */
export const DEFAULT_EXECUTION: RunExecution = 'portfolio'
/** How much prose an autonomous portfolio authors for a new leaf immediately. */
export type RunDetail = 'full' | 'progressive'
/**
 * Whether a run takes its next move on its own or proposes it and waits.
 *
 * `auto` is the run this app always had — the one you can walk away from.
 * `confirm` holds the same loop at the gap between passes: the move the policy
 * picked is published, priced, and nothing runs until the person says go. The
 * proposal comes from the policy's own tables, never from a model, so waiting
 * spends nothing — and the wait has no countdown, because a timer that runs a
 * pass when it expires is auto mode wearing a delay.
 */
export type RunMode = 'auto' | 'confirm'
/**
 * Which group of controls the settings sheet lands on.
 *
 * Named at the door and never remembered. A control that means "change where
 * source checks are routed" has to arrive at the routing rows; a section restored
 * from whatever was last read would send it somewhere else, and the sheet is long
 * enough that landing in the wrong place reads as the control having done nothing.
 */
export type SettingsSection = 'providers' | 'routing' | 'defaults' | 'appearance' | 'sources'

/**
 * What a refused pass needed a key for, in the words of the one sentence the
 * card prints: `what` runs on `on` — "The source check" on the routed model's
 * name, or "A cross-examination" on "another company’s model" where no one model
 * is meant. Plain data, like everything else a refusal leaves in the store.
 */
export interface KeyNeed {
  readonly what: string
  readonly on: string
}

/**
 * Why the settings sheet is open on one house's key field. `rejected`: the key is
 * there and the provider turned it away on a pass (D193). `missing`: there is
 * none, for the house the refused pass resolved to, and `need` says what needed
 * it.
 */
export type KeyAsk =
  | { readonly provider: ProviderId; readonly why: 'rejected' }
  | { readonly provider: ProviderId; readonly why: 'missing'; readonly need: KeyNeed }

export type ResearchTab =
  | 'findings'
  | 'protocol'
  | 'evidence'
  | 'tests'
  | 'versions'
  | 'tournament'
  | 'audit'
  | 'mathematics'
export type ExportSection = 'capsule'

/**
 * What the NEXT run is set to do, minus the ceiling (`runCeiling`, which predates
 * this and keeps its own field for the same reason this one exists).
 *
 * In the store for the reason the ceiling is: the controls that read and write it
 * live in surfaces that unmount — the tune panel closes, the composer's chips come
 * and go — and a run spec silently reset by closing a popover would make the next
 * press do something its owner had already un-chosen. `checks: undefined` means
 * "whatever the move count implies", which keeps the default run identical to one
 * configured before this state existed.
 */
export interface RunTune {
  moves: number
  checks: number | 'every' | undefined
  panels: number
  audits: number
  /** Absent and zero are deliberately equivalent: no conjecture passes, the
   *  default (D281). Not in `TuneDefaults` — a run conjectures only when asked. */
  conjectures?: number
  /** Absent and `DEFAULT_EXECUTION` are deliberately equivalent. */
  execution?: RunExecution
  /** Progressive leaves are valid only with portfolio execution. */
  detail?: RunDetail
  /** Absent and `auto` are deliberately equivalent, like `execution`. */
  mode?: RunMode
  ladder?: readonly DeriveOp[]
  depth?: number
  until?: UntilCondition
}

/**
 * The counts a new exploration starts from — what `runTune` is seeded with at
 * boot, rather than what any particular run is set to.
 *
 * Only what a run's arithmetic reads. No ceiling, on purpose: see `runCeiling`,
 * which is deliberately not persisted because a limit restored silently weeks
 * later is applied to a run nobody remembers configuring. A default that carried
 * one would be a single refactor away from exactly that.
 *
 * Every field optional, and an absent one means "whatever this build ships",
 * which is the same state as never having touched the tune panel. That is what
 * lets the defensive parser drop a field it does not like rather than reject the
 * whole blob and lose the three beside it.
 */
export interface TuneDefaults {
  moves?: number
  checks?: number | 'every'
  panels?: number
  audits?: number
  execution?: RunExecution
  detail?: RunDetail
  mode?: RunMode
}

/**
 * `runTune` as it boots from a set of defaults — the ONE expression of the
 * shipped fallbacks. Boot reads it, and the composer's committed chips compare
 * against it to decide which steering has strayed far enough from "untouched" to
 * earn a pill — so a second copy of these numbers would let a chip mark the boot
 * state as steering, or steering as the boot state, the moment one copy moved.
 */
export const seededTune = (d: TuneDefaults): RunTune => ({
  moves: d.moves ?? 24,
  checks: d.checks,
  panels: d.panels ?? 0,
  audits: d.audits ?? 0,
  execution: d.execution ?? DEFAULT_EXECUTION,
  detail: d.detail ?? 'full',
  mode: d.mode ?? 'auto',
})

/**
 * What the NEXT run is set to do, minus the ceiling (`runCeiling`, which predates
 * this and keeps its own field for the same reason this one exists).
 *
 * In the store for the reason the ceiling is: the controls that read and write it
 * live in surfaces that unmount — the tune panel closes, the composer's chips come
 * and go — and a run spec silently reset by closing a popover would make the next
 * press do something its owner had already un-chosen. `checks: undefined` means
 * "whatever the move count implies", which keeps the default run identical to one
 * configured before this state existed.
 */
export interface RunTune {
  moves: number
  checks: number | 'every' | undefined
  panels: number
  audits: number
  ladder?: readonly DeriveOp[]
  depth?: number
  until?: UntilCondition
}

export interface Toast {
  id: string
  text: string
  tone: 'info' | 'good' | 'warn' | 'bad'
  /**
   * An inline recovery, for the toasts that used to name a keyboard shortcut as
   * the way back ("⌘Z to put it back") — a route that does not exist on touch.
   * The action IS the affordance; the keystroke keeps working beside it.
   *
   * `run` returns `false` to REFUSE — the toast then stays on screen, so the
   * offer survives a tap that could not be honoured and can be taken again once
   * whatever blocked it clears. Anything else (including `void`) consumes the
   * toast, which is what taking the offer means. See `Toasts` in ui/Dialogs.tsx.
   */
  action?: { label: string; run: () => boolean | void }
}

/**
 * What the last breakthrough scan ran over: how many nodes, under which temper,
 * on which model. The scan control compares the current graph and reading
 * against this to say "nothing would change" BEFORE anything is spent — a
 * second pass over the same graph under the same reading returns the same
 * pairs, and charging for it would be charging for a re-read.
 *
 * In memory only, deliberately. Node count and model survive a reload anyway
 * (the last bridge op carries both), but the temper it ran under is recorded
 * nowhere durable — so after a reload the control errs toward offering the
 * scan rather than claiming, on a fact it no longer holds, that one would
 * change nothing.
 */
/**
 * A proposed move list, and the CLAIMS it was proposed against.
 *
 * The `lastScan` rule, at the price of a paid pass: the list names claims by id
 * and gives a reason per step, both of which are about the graph as it stood
 * when the plan pass ran. Held without a stamp it outlived the graph — it
 * survived an edit, and `startSession`, `restoreSession` and `clearSession`
 * left it alone entirely, so it could be approved against a different
 * exploration whose node ids happen to match. Approving it then spends on
 * targets and a rationale nobody proposed for these claims.
 *
 * `claims` is a digest of the wording of the nodes the list NAMES, and it is
 * deliberately not `rev`. The revision counter is not a graph identity: pane
 * toggles and window resizes move it through `setViewInset`, which bumps it on
 * purpose so the camera re-follows — so stamping with `rev` retired a paid list
 * because somebody opened the sidebar. It is also too coarse in the other
 * direction, retiring a list over an edit to a claim it never mentions. The
 * digest answers the question the list actually depends on: is the wording each
 * step was reasoned about still the wording on the graph?
 *
 * A step naming a node that has since been PRUNED is not counted, because
 * `nextProgrammeStep` already skips one and skipping is not staleness.
 *
 * AND IT IS THE CLAIMS THE LIST NAMES, not every claim the plan turn showed.
 * `buildPlanTurn` prints the whole live set, so it is true that a change to a
 * claim the list never mentions could have produced a different list — a step
 * on that claim, or another order. Stamping the live set was considered and
 * refused, because the costs are not symmetric. A list that is out of date about
 * a claim it does not name is still executable and still accurate about its own
 * targets: what you lose is a list you might have preferred. A list retired
 * because something moved elsewhere on the graph is a PAID pass thrown away, and
 * spuriously retiring paid lists is the failure this stamp has actually caused —
 * see the `rev` paragraph above, which refuses the same coarseness for the same
 * reason.
 *
 * THE ACCEPTED COST, stated because it is real: a step's `why` sentence can
 * reason about a claim the step does not name — "check this, nothing on the
 * branch has been" — and that sentence can go stale while this digest holds. The
 * list still runs what it says it runs; the reason it gives for one step may
 * have been overtaken. Widening the stamp is the only thing that would catch it,
 * and it would retire far more lists than it saved.
 */
export interface ProgrammeDraft {
  programme: Programme
  sessionId: string
  claims: string
}

/**
 * EVERYTHING the plan turn showed the model about the claims a list names, in
 * step order, as one string. Pruned targets are left out — see `ProgrammeDraft`.
 *
 * The list is a proposal about what the model was shown, so the stamp has to
 * cover what it was shown and not a chosen part of it. `buildPlanTurn` prints
 * `line(n)` and then `done(n)`, so this is `claimMarks` — kind, dead end,
 * user-revised, `measured:` — plus the confidence to the two places the turn
 * rounds it to, the label, the claim, and `evidenceMarks`.
 *
 * Read off those two functions rather than restated, because this stamp has
 * been wrong twice by naming a subset. First it hashed the wording alone, so a
 * source check run between planning and approval left the sheet calling the
 * list fresh and the approval paid for the check twice. Then it learned
 * `evidenceMarks` and still ignored the marks printed one line above them — so
 * a recorded outcome, the one thing in the model's context that is not its own
 * prose, could land between the paid pass and the press while an `experiment`
 * proposed in its absence stayed approvable.
 */
export function programmeClaimKey(nodes: Session['nodes'], p: Programme): string {
  return fnv1a64(
    p.steps
      .map((step) => {
        const n = nodes[step.nodeId]
        return n
          ? [
              step.nodeId,
              n.label,
              n.claim,
              claimMarks(n).join(', '),
              `conf ${n.confidence.toFixed(2)}`,
              evidenceMarks(n),
            ].join('\u0000')
          : ''
      })
      .filter(Boolean)
      .join('\u0001'),
  )
}

export interface ScanNote {
  nodes: number
  temper: Temper
  modelId: string
  /**
   * What the scan read beyond this graph — `scanPoolKey` over the digests the
   * pass actually printed, `''` for a within-graph pass. A different pick of
   * explorations, or a foreign one saved since, is a different reading and not
   * a re-read, so it belongs in the same comparison the temper and model are in.
   */
  pool: string
}

/**
 * What the last breakthrough scan ran over: how many nodes, under which temper,
 * on which model. The scan control compares the current graph and reading
 * against this to say "nothing would change" BEFORE anything is spent — a
 * second pass over the same graph under the same reading returns the same
 * pairs, and charging for it would be charging for a re-read.
 *
 * In memory only, deliberately. Node count and model survive a reload anyway
 * (the last bridge op carries both), but the temper it ran under is recorded
 * nowhere durable — so after a reload the control errs toward offering the
 * scan rather than claiming, on a fact it no longer holds, that one would
 * change nothing.
 */
export interface ScanNote {
  nodes: number
  temper: Temper
  modelId: string
}

/**
 * What a conversation is pinned to.
 *
 * A node or a candidate synthesis, because "chat with this" is asked of both and a
 * candidate is a PAIR rather than a node — it has two ends, one of which may live
 * in another exploration. Pinned rather than following the selection: a question
 * you asked about one claim must not silently become a question about whichever
 * node you clicked next.
 */
export type ChatTarget =
  | { kind: 'node'; id: string }
  | { kind: 'bridge'; id: string }
  // The whole graph at once — what the composer pins when nothing is selected.
  | { kind: 'graph' }

/**
 * What a live run looks like to the UI. A projection, not the run's state.
 *
 * The loop keeps its own closed sets and attempt maps in its own module, because
 * they are Maps of Sets and this object is read by React on every change. Every
 * field here is a count of something that happened or the move in flight — there
 * is no progress fraction and nothing that could read as a verdict on the graph.
 */
export interface RunLive {
  id: string
  budget: RunBudget
  movesRun: number
  /** Distinct operation records: one for a portfolio, however many moves it owns. */
  passesRun: number
  /** What is running right now, or null in the gap between passes. */
  now: {
    kind: LogicalOpKind
    nodeId: string
    label: string
    why: MoveReason
    /** Several logical angles sharing the one model pass now in flight. */
    packet?: RunVerb[]
  } | null
  /** `waiting` is confirm mode's pause: the move in `now` is PROPOSED — picked
   *  by the policy, published, and not running — until the person answers. */
  phase: 'moving' | 'waiting' | 'ended'
  /** Which mode the loop is in NOW, not which it started in: standing approval
   *  ("On its own from here") flips a confirm run to auto mid-flight. */
  mode: RunMode
  stop: RunStop | null
  open: RunOpen
  counts: {
    nodes: number
    dead: number
    /** `dead` by the ending each reached — what the strip prints (`runEndingsLine`).
     *  Optional only so a hand-built state need not spell it; the loop always does. */
    endings?: ReasonedEndingCounts
    nothing: number
    refused: number
    failed: number
    checks: number
    declined: number
  }
  /**
   * What the run has drawn, estimated, against the ceiling it was given.
   *
   * `usd` is an upper estimate from list prices and never a bill — every surface
   * that prints it has to say so, which is why the number arrives here beside
   * `priced` rather than as a bare figure. `priced` is false for a model this app
   * has no rate for: the estimate is then meaningless rather than zero, and a
   * surface printing "$0.00" over a run on someone's own gateway would be the one
   * lie this whole arrangement exists to avoid.
   */
  spend: { usd: number; ceiling: number | null; priced: boolean }
  /** Set when one branch has taken its share of the budget, so the pod can say so
   *  rather than appear to have lost interest. */
  capNote: string | null
  /** Present while the run waits before its next move, after one failed on the
   *  API's side or the wire's — what it met and how long is left (`pauseAfter` in
   *  `engine/run.ts`). Never stored: the record has the failed move itself. */
  pause?: { cause: WaitCause; msLeft: number }
}

/**
 * The workspace's own slots.
 *
 * The keys live HERE and only here. Never in `nv.prefs` — the autosave
 * subscription re-serialises that blob on every rail toggle — and never in
 * `persist.ts`'s `K`, which `forgetAll()` iterates: one "forget everything"
 * gesture must not be able to sweep a secret the user pasted, because the user
 * cannot tell whether it was swept until the next pass fails.
 */
const LS = {
  theme: 'nv.theme',
  key: (p: ProviderId) => `nv.key.${p}`,
  model: 'nv.model',
  /** Per-role model overrides, as a JSON object. Beside `nv.model` rather than in
   *  `nv.prefs` for the reason given on `setModel`: a debounced write that never
   *  fires would mean the next boot reasons somewhere the user did not choose. */
  roles: 'nv.roles',
  /** The custom endpoint's address and the model name to send it. Beside the keys
   *  and not in `nv.prefs`, for the same reason: a debounced write that never
   *  fires would leave the next boot pointing at nothing. */
  customBase: 'nv.custom.base',
  customModel: 'nv.custom.model',
  /** Which API the custom endpoint speaks. Beside the address for the reason
   *  the address is here: it changes where the next request goes. */
  customDialect: 'nv.custom.dialect',
  /**
   * The counts a new exploration starts from. In LS and not `nv.prefs` for the
   * reason the keys are: this changes what the next pass DOES and what it costs,
   * and a debounced prefs write that never fires would run something nobody
   * chose. Deliberately carries NO spend ceiling — see the `runCeiling` comment.
   */
  tune: 'nv.tune',
  /**
   * JSON `{ [kind of work]: level }` — how hard each kind of work thinks, as the
   * person set it (D313). Beside `nv.model` and for the same reason: it decides
   * what the next pass draws, and a debounced prefs write that never fired would
   * run the next boot at a level nobody chose. Validated at boot, entry by entry.
   */
  effortLevels: 'nv.effortLevels',
  /**
   * The named effort profile the app ran under before D313. Read only while
   * `nv.effortLevels` is absent, to carry a profile somebody chose into the
   * levels it meant, and removed the first time a level is set.
   */
  effort: 'nv.effort',
  /** JSON `{ [sourceId]: boolean }`. Beside the keys rather than in `nv.prefs`
   *  for the reason the keys are: a debounced write that never fires would leave
   *  the next boot reading from sources nobody chose. */
  sourcesOn: 'nv.src.on',
  /** A contact address for the polite pools, NOT a secret — it is sent as a
   *  `mailto=` parameter and is the opposite of a key. Here anyway, because it is
   *  written the moment it is typed like everything else in this table. */
  sourceMailto: 'nv.src.mailto',
  /**
   * The name on the sidebar's profile row. In this table rather than `nv.prefs`
   * for a reason the rest of this list shares but which is sharpest here:
   * `loadPrefs` returns null for the WHOLE blob on a parse failure. For a lens
   * that is a taste reset; for a name somebody typed about themselves it is a
   * silent deletion, and they cannot tell it was deleted rather than never saved.
   * Not a secret, and not swept by `forgetAll` — a name is not a stored graph.
   */
  profileName: 'nv.profile.name',
  /**
   * Projects declared and not yet filled — see `core/projects.ts` for why they
   * need a slot at all and why it is the smallest one that works.
   *
   * In this table rather than `nv.prefs` for the reason the name above is, and
   * more sharply: a prefs blob that fails to parse is discarded WHOLE, so one
   * bad lens would silently take every empty project somebody had set up with
   * it. It is also written the instant a name is typed, which a debounced blob
   * cannot promise. Not a secret, and not swept by `forgetAll` — a label is not
   * a stored graph, and a device that forgot every exploration keeps the shape
   * the user had arranged them in.
   */
  projects: 'nv.projects',
  /** Where a single-provider build kept its one key. Lifted once, then removed. */
  legacyKey: 'nv.apiKey',
}

function readLS(k: string): string | null {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
function writeLS(k: string, v: string | null) {
  try {
    if (v === null) localStorage.removeItem(k)
    else localStorage.setItem(k, v)
  } catch {
    /* private mode — settings just won't persist */
  }
}

/**
 * Move a key written by the single-provider build into the Anthropic slot.
 *
 * That slot could only ever have held an Anthropic key, so this is a lift and
 * not a guess. It is idempotent BY DELETION rather than by a flag: a "migrated"
 * flag would outlive the key it describes, and a user who then removed the key
 * through the sheet would find it back after the next reload — the one behaviour
 * that would make "Remove" untrustworthy.
 *
 * Silent on purpose: moving the key changes nothing about which model runs. The
 * single-provider build never wrote a model choice, so this is a workspace that
 * chose nothing, and it runs on `DEFAULT_MODEL_ID` like every other — Opus 5.5
 * since D109, where its graph ran on Opus 5. The sidebar marks the model the
 * next pass runs on, and every pass is stamped with the one that produced it, so
 * the graph still says which is which.
 */
function liftLegacyKey() {
  const old = readLS(LS.legacyKey)
  if (!old) return
  if (!readLS(LS.key('anthropic'))) writeLS(LS.key('anthropic'), old)
  writeLS(LS.legacyKey, null)
}

// Re-exported so the engine can keep importing both from the store, which is
// where they lived before the persistence codec needed them without it.
export { emptySession, makeNode } from './session'

/**
 * How much of a reasoning trace we keep in memory.
 *
 * The live ticker keeps the last 600 characters; this keeps the first 8000.
 * Summarised thinking for these calls runs roughly 1,500–5,000 characters, so
 * clipping should be rare — and the *head* is the part worth keeping, because a
 * summarised trace opens with the framing and with which constraint it decided
 * actually binds. The on-disk copy is clipped harder; this number is a reading
 * decision, not a storage one.
 */
export const THINKING_CAP = 8000

/** Steps of history. Each entry is a few hundred pointers, so this is free. */
const UNDO_DEPTH = 30

export interface UndoEntry {
  /** The verb, for the toast: 'Prune', 'Rewrite', 'Challenge'… */
  label: string
  session: Session
  selectedId: string | null
  focusId: string | null
  rightTab: RightTab
}

function snapshot(s: State, label: string): UndoEntry {
  const ses = s.session
  return {
    label,
    // Node objects are shared on purpose: `patchNode` replaces a node rather
    // than mutating it, so the only fields that can drift are the scratch
    // coordinates `layout()` writes — and `restore` relayouts unconditionally.
    session: {
      ...ses,
      nodes: { ...ses.nodes },
      order: ses.order.slice(),
      bridges: ses.bridges.slice(),
      ops: ses.ops.slice(),
    },
    selectedId: s.selectedId,
    focusId: s.focusId,
    rightTab: s.rightTab,
  }
}

/**
 * Append a complete semantic board without making the history contain itself.
 * `semanticSnapshot` excludes knowledge history by contract, so the retained
 * states stay bounded instead of growing recursively on every event.
 */
function withKnowledgeRevision(
  session: Session,
  reason: string,
  options: { eventId?: string; actor?: SemanticActor; at?: number } = {},
): Session {
  const at = options.at ?? Date.now()
  const research =
    session.research ??
    createResearchState({ id: `research_${session.id}`, now: session.createdAt })
  const base = { ...session, research }
  const revision = captureKnowledgeRevision(base, {
    id: uid('known'),
    recordedAt: at,
    reason,
    ...(options.eventId ? { eventId: options.eventId } : {}),
    ...(options.actor ? { actor: options.actor } : {}),
  })
  return {
    ...base,
    research: {
      ...research,
      knowledgeHistory: appendKnowledgeRevision(research.knowledgeHistory, revision),
    },
  }
}

type Setter = (p: Partial<State>) => void

function restore(set: Setter, get: () => State, entry: UndoEntry) {
  const cur = get().session
  const now = Date.now()

  // Ids present in exactly one of the two graphs.
  const a = new Set(cur.order)
  const b = new Set(entry.session.order)
  const changed = new Set<string>()
  for (const id of cur.order) if (!b.has(id)) changed.add(id)
  for (const id of entry.session.order) if (!a.has(id)) changed.add(id)

  // Anchors: surviving parents of whatever changed, plus the restored
  // selection. Always a handful — pulsing forty nodes is a flash, not a signal.
  const anchors = new Set<string>()
  for (const id of changed) {
    const par = entry.session.nodes[id]?.parentId ?? cur.nodes[id]?.parentId ?? null
    if (par && entry.session.nodes[par] && !changed.has(par)) anchors.add(par)
  }
  if (entry.selectedId && entry.session.nodes[entry.selectedId]) anchors.add(entry.selectedId)

  const nodes = { ...entry.session.nodes }

  // A node can disappear when undo crosses the pass that created it. Its source
  // checks still happened and their op/spend rows are carried below, so move
  // those events into the session archive before restoring the older graph.
  const removedChecked = cur.order.filter((id) => !entry.session.nodes[id])
  const verificationArchive = mergeVerificationArchive(
    entry.session.verificationArchive,
    cur.verificationArchive,
    archiveVerificationsFor(cur, removedChecked),
  )

  // Source checks are observations that actually happened, like the spend and
  // op rows carried forward below. Undo may withdraw a branch or a rewrite, but
  // it must not delete a paid evidence event and leave only its receipt behind.
  // The live ledger is normally a strict extension of the snapshot's; the union
  // also makes redo and an imported hand-edited document deterministic.
  for (const id of entry.session.order) {
    const held = nodes[id]
    const live = cur.nodes[id]
    if (!held || !live) continue
    const unique = mergeVerificationEvents(verificationEvents(held), verificationEvents(live))
    if (!unique.length) continue
    const verification = unique[unique.length - 1]!
    nodes[id] = {
      ...held,
      verification,
      verificationHistory: unique.length > 1 ? unique.slice(0, -1) : undefined,
    }
  }

  // The seed's survey, by the same rule: a search that happened, carried
  // forward whole. The seed never leaves the graph, so there is no archive to
  // move it into — the two ledgers are simply unioned, the live one last.
  for (const id of entry.session.order) {
    const held = nodes[id]
    const live = cur.nodes[id]
    if (!held || !live || held.parentId !== null) continue
    const surveys = mergeSurveyEvents(surveyEvents(held), surveyEvents(live))
    if (!surveys.length) continue
    nodes[id] = {
      ...held,
      survey: surveys[surveys.length - 1],
      surveyHistory: surveys.length > 1 ? surveys.slice(0, -1) : undefined,
    }
  }

  // Fold state and hand-placement are carried FORWARD from the live session
  // rather than rolled back, the same rule as the ledger below: undo edits the
  // argument, not what it cost and not what you have put away. Without this,
  // one step back after a prune silently unfolds every branch folded since —
  // and throws away every node the reader had pulled out to read.
  for (const id of entry.session.order) {
    const live = cur.nodes[id]
    const n = nodes[id]
    if (!live || !n) continue
    if (live.collapsed === n.collapsed && live.foldAt === n.foldAt && live.stretch === n.stretch)
      continue
    nodes[id] = { ...n, collapsed: live.collapsed, foldAt: live.foldAt, stretch: live.stretch }
  }
  // Copy, never mutate: redo may hand this very entry back.
  for (const id of anchors) {
    const n = nodes[id]
    if (n) nodes[id] = { ...n, lastPulse: now }
  }

  // Candidates the scan found *after* this step was marked are not part of the
  // argument either — the scan takes no step of its own, so rolling them back
  // would silently discard a paid pass. But a candidate the entry still holds
  // and the live session does not was realised, and undoing that must bring it
  // back. So: the entry's set, plus anything live it does not know about.
  const known = new Set(entry.session.bridges.map(pairKey))
  const bridges = [
    ...entry.session.bridges,
    // `pairKey` is order-insensitive and end-aware. A template literal over
    // `aId`/`bId` used to live here; against a foreign end it would collapse to
    // one constant string and merge every cross-exploration candidate into one.
    ...cur.bridges.filter((b) => !known.has(pairKey(b)) && bridgeReadable(b, nodes)),
  ]

  const session: Session = {
    ...entry.session,
    nodes,
    bridges,
    // Whole-board replay is a ledger of states that existed, not an argument
    // field to erase with undo. Restore the old meaning but carry the dated
    // ledger forward; the caller appends the just-restored state immediately.
    research: entry.session.research
      ? {
          ...entry.session.research,
          knowledgeHistory:
            cur.research?.knowledgeHistory ?? entry.session.research.knowledgeHistory,
          // Execution attempts are historical spending facts, not undoable argument state.
          ...(cur.research?.agenda ? { agenda: cur.research.agenda } : {}),
          ...(cur.research?.experiments ? { experiments: cur.research.experiments } : {}),
          ...(cur.research?.mathematics ? { mathematics: cur.research.mathematics } : {}),
          ...(cur.research?.assessments ? { assessments: cur.research.assessments } : {}),
          ...(cur.research?.mathematicsInvalid ? { mathematicsInvalid: true as const } : {}),
          ...(cur.research?.computationalWorkspace
            ? { computationalWorkspace: cur.research.computationalWorkspace }
            : {}),
        }
      : cur.research,
    // The ledger is not part of the argument. The ops really ran and the tokens
    // really were spent — and the *identity* is not either: a snapshot taken
    // before an op re-minted a pinned demo id would otherwise hand that id
    // back, and the next autosave would write over the pristine demo.
    id: cur.id,
    ops: cur.ops,
    // Node ordinals are evidence chronology, not graph content. Crossing an
    // undo boundary may remove the node that consumed one, but must never make
    // that ordinal available to a later claim.
    nextNodeOrdinal: Math.max(
      cur.nextNodeOrdinal ?? 0,
      entry.session.nextNodeOrdinal ?? 0,
      entry.session.order.reduce(
        (max, id) => Math.max(max, (nodes[id]?.createdOrdinal ?? -1) + 1),
        0,
      ),
    ),
    verificationArchive,
    // A run really ran. Undo withdraws the nodes it threaded and must not withdraw
    // the account of what it did to get them: the reason it stopped, and how much
    // it left open, cannot be recovered from a graph.
    runs: cur.runs,
    // The whole ledger, for the same reason and with the same force: those passes
    // were billed, searches included. A step back that reset the tally would make
    // the sidebar under-report what the exploration actually cost — and now that
    // the tally is per model, the whole of it would go and not just a count.
    spend: cur.spend,
  }

  // Mandatory: `layout()` writes coordinates onto the shared node objects, so
  // both the stored positions and the cached bounds are stale by now.
  const bounds = layout(session.nodes, session.order)

  // Folds were carried forward, so a restored selection can be sitting inside
  // one. Hand it to the outermost stub that hides it, the way toggleCollapse
  // does — a selection you cannot see is a selection you cannot act on.
  const surface = (id: string | null): string | null => {
    if (!id || !session.nodes[id]) return null
    let out = id
    let cur2 = session.nodes[id]!.parentId
    const guard = new Set<string>()
    while (cur2 && !guard.has(cur2)) {
      guard.add(cur2)
      const p = session.nodes[cur2]
      if (!p) break
      if (p.collapsed) out = cur2
      cur2 = p.parentId
    }
    return out
  }

  set({
    session,
    bounds,
    rev: get().rev + 1,
    selectedId: surface(entry.selectedId),
    focusId: surface(entry.focusId),
    rightTab: entry.rightTab,
    // A scrub index only means something relative to a given `order`.
    playhead: null,
    playing: false,
    // A step back is a different graph arriving, and the coach's reading of the
    // one that just left is spent — its cues can name nodes this restore withdrew.
    coach: null,
  })
}

function clipThinking(raw?: string): { thinking?: string; clipped?: boolean } {
  if (!raw) return {}
  const t = raw.replace(/\n{3,}/g, '\n\n').trim()
  if (!t) return {}
  if (t.length <= THINKING_CAP) return { thinking: t }
  return { thinking: `${t.slice(0, THINKING_CAP).trimEnd()}…`, clipped: true }
}

/**
 * The coach's half of the dismissal idiom: the card and the digest go away, and
 * nothing else does.
 *
 * One helper rather than thirty inline copies, because thirty copies of a
 * three-field spread is how one of them comes to leave the WHY fold open. The
 * retirement maps and the bar are untouched here on purpose — closing a card is
 * not answering its cue, and the bar reports a run that did still end.
 */
const calmedCoach = (c: State['coach']): State['coach'] =>
  c && (c.open || c.digest || c.why) ? { ...c, open: false, digest: false, why: false } : c

/**
 * Whether the coach's doors refuse right now — one reading for both of them, the
 * card (`openCoach`) and the list (`openCoachDigest`), so a press cannot open one
 * where it would refuse the other.
 */
const coachDoorShut = (st: Pick<State, 'busy' | 'run' | 'session' | 'playhead'>): boolean =>
  // A moving graph already has an author of attention. The coach becomes
  // available again when that work settles, without competing for the camera.
  // A run WAITING at its confirm gate is still that author: the pause is a
  // question on screen, and a coach opened over it would compete with the one
  // thing the person is being asked to answer.
  st.busy ||
  (st.run !== null && st.run.phase !== 'ended') ||
  // The dock spark's own floor, so the two doors agree: on a seed with nothing
  // grown yet there is no cue to show and no ending to report, and a bar over
  // an empty field would be chrome about nothing. The keyboard refuses quietly;
  // the spark is where the refusal wears its sentence.
  st.session.order.length < 2 ||
  // And the render gate's other arm: rewound, every coach surface is hidden,
  // so a press here would return "opened" while showing nothing — then spring
  // the bar and pins on the reader whenever they scrub back to now. A press
  // that arms invisible chrome is worse than one that refuses.
  (st.playhead !== null && st.playhead < st.session.order.length)

interface State {
  session: Session
  bounds: Bounds
  /** Bumped on every structural change; the renderer keys its caches off this. */
  rev: number
  selectedId: string | null
  hoverId: string | null
  focusId: string | null

  view: Viewport
  /**
   * Where the camera is heading. The render loop eases `view` toward this
   * each frame; nothing sets `view` to a new framing directly, because a graph
   * that re-frames on every arriving node makes the canvas jump under you.
   */
  /**
   * Which canvas is mounted: the flat one, or the spatial one.
   *
   * Two views of one graph, and this is the only piece of state that says which.
   * Everything downstream of it — visibility, folds, focus, selection, the label
   * layer — is shared deliberately, so switching is a change of camera and never a
   * change of what is on screen.
   */
  viewMode: '2d' | '3d'
  /** What sizes a claim on the canvas — see `core/weight.ts`. Read by
   *  `useGraphFrame`, which hands the renderer a substituted map under
   *  `evidence` and the session's own under `model`. */
  weighting: Weighting
  /**
   * The instruments switch: one preference behind which the research workspace,
   * the several-passes tier, the compiled diagnostics and the heaviest exports
   * sit. OFF is EXPLORER MODE, which is what a fresh workspace starts in.
   *
   * It hides surfaces and never disables anything: a claim can still be
   * panelled or cross-examined from the palette, and every export format is
   * still built by the same code. Wherever something is hidden, the surface
   * says so in a line and names the switch — a tool that vanished with no
   * sentence is a tool the person will assume they broke.
   */
  instruments: boolean
  /** The graph footer's view switches (D369), persisted with the prefs: the
   *  depth rings, the dimming of what the selected claim does not stand on or
   *  hold up, and the minimap. */
  showRings: boolean
  dimUnrelated: boolean
  showMinimap: boolean
  /**
   * Where the spatial camera is.
   *
   * Kept beside `view` rather than inside it because they are not the same kind of
   * thing and cannot be unified: one is a 2D affine transform, the other is an
   * orbit. A single "camera" field would have to be a union, and every reader
   * would branch on the mode anyway.
   */
  cam: Cam
  /**
   * Where the spatial camera is heading, or null when it is where it wants to be.
   *
   * The same arrangement as `viewTarget` and for a stronger reason: in a volume an
   * instant cut to a new vantage point costs the viewer their orientation
   * completely, where in a flat pan it merely surprises them. Every deliberate
   * move eases; every direct manipulation clears this and takes over.
   */
  camTarget: Cam | null
  viewTarget: Viewport | null
  /**
   * True once the user has moved the camera themselves. Until then the view
   * follows the growing graph, so an exploration frames itself as it expands
   * instead of running off the edge of the screen.
   *
   * The one guardrail every camera writer reads: a pass LANDING never moves a
   * pinned view. `follow` refuses on it itself, rather than trusting each loop
   * that calls it to ask first. What may still move a pinned view is the reader
   * — a pan, a Fit, a node they picked from a list (`lookAt`).
   */
  viewPinned: boolean
  /**
   * A node somebody picked from a list and now wants to see, or null.
   *
   * A REQUEST, not a camera: the store does not know the stage's size, and the
   * render loop is the only place with one it can trust (see NeuralCanvas). So a
   * press from the thread, the palette, the search sheet or the node tree asks
   * here, and the loop answers once on its next frame through `settleLook` —
   * easing to the node only if it is outside the visible field, so a pick of a
   * node already in view moves nothing at all.
   */
  viewAsk: string | null
  /**
   * The pass that opened the graph has landed, and the next follow frames it
   * whole rather than waiting for it to leave the field (`openingView`).
   *
   * A request, as `viewAsk` is, answered once by the loop's next `follow` — and
   * only there, so the pinned view it must never move is refused where every
   * landing is refused. `endOp` raises it; `follow` lowers it whether it framed
   * or refused; the next pass and a new exploration drop it unanswered.
   */
  frameOpening: boolean
  filters: Filters

  theme: Theme
  lens: Lens
  temper: Temper
  /**
   * How hard each kind of work thinks, as the person set it (D313) — see
   * `EffortWork` and `levelFor` in `core/models.ts`, and `engine/effort.ts` for
   * which kind each pass is. SPARSE, like `roleModels`: an absent kind thinks at
   * its default. Workspace-level like `modelId` and for the same reason: the
   * per-pass fact is the effort each call actually ran at, and that lives on the
   * call receipt (D69). This field only answers "what happens if you press D now".
   */
  effortLevels: EffortLevels
  /**
   * Which model runs the NEXT pass. Workspace-level, not per session.
   *
   * A session does not carry it, because the honest per-session fact is which
   * model produced each record — and that lives on `Op`, `ReasonNode`,
   * `Verification` and `PanelCheck`, where it can differ within one graph. This
   * field only answers "what happens if you press D now".
   *
   * A plain `string` and not a closed union: a union buys no exhaustiveness (the
   * one literal call site is `DEFAULT_MODEL_ID`) and it breaks the moment a
   * response returns a dated snapshot id. Validated through `findModel` at boot.
   */
  modelId: string
  /**
   * Where a particular kind of work goes, when it should not go to `modelId`.
   *
   * SPARSE, and that is the whole design. An absent role means "the selection",
   * so the default state of this map is `{}` and the app behaves exactly as it
   * did before routing existed. A full `Record<Role, string>` would have to be
   * initialised with the selected model in every slot, and would then go stale
   * the moment someone changed the selection — every role silently pinned to
   * whatever model happened to be chosen the first time they opened the app.
   *
   * Ids are validated at boot through `findModel` and dropped if unknown, for the
   * same reason `modelId` is: a delisted model here would resolve to nothing and
   * turn one feature off with no explanation attached to it.
   */
  roleModels: Partial<Record<Role, string>>
  /**
   * One key per provider, both slots always present.
   *
   * `Record<…, string | null>` and not `Partial<Record<…>>`: with
   * `exactOptionalPropertyTypes` off, an optional property is not even checked
   * for the difference between "not set" and "misspelled the field name", and
   * that difference is the one this whole area exists to protect.
   */
  keys: Record<ProviderId, string | null>
  /**
   * The custom endpoint's base URL, and the model name to send it.
   *
   * Two plain strings the user typed. They are not validated against anything —
   * there is nothing here to validate them against — so every surface that names
   * where a request goes reads the URL itself rather than a provider record.
   */
  customBase: string
  customModel: string
  /**
   * Which OpenAI-shaped API the custom endpoint speaks. `responses` is what
   * every custom endpoint was until this existed; `chat` is Chat Completions,
   * which is what Ollama and LM Studio serve, and picking it is what makes a
   * free local model usable here. Read by `resolveModel` and by nothing else —
   * the adapter branches on the resolved model, never on the store.
   */
  customDialect: 'responses' | 'chat'

  railRight: boolean
  /** Whether the explorations sidebar is showing as its icon rail. Persisted in
   *  `Prefs`, so a workspace opens the way it was left — and false there on
   *  anything unrecognised, because the list is the way to a stored exploration. */
  sidebarCollapsed: boolean
  /**
   * How wide the reader dragged the thread column, or null for "never said".
   *
   * The number the SEPARATOR writes, and nothing else in the app reads it raw:
   * `threadWidthFrom` in `ui/threadPane.ts` holds it inside what the window
   * allows, on every read, so a column dragged wide on a big monitor cannot pin
   * the canvas shut on a laptop. Null is a setting's absence rather than a
   * width, which is what keeps an untouched pane following the stage share.
   */
  threadWidth: number | null
  /**
   * How wide the reader dragged the explorations sidebar, or null for "never
   * said" — the thread's own rule, for the other pane (D104). Nothing reads
   * it raw: `sidebarWidthFrom` in `ui/sidebarPane.ts` holds it inside what the
   * window allows on every read, so a pane dragged wide on a big monitor
   * cannot pin the field shut on a laptop. Null is the setting's absence, and
   * reads as the width the pane always had.
   */
  sidebarWidth: number | null
  /**
   * The width at which every name in the explorations sidebar reads in full,
   * measured by the pane off its own rows after each render, or null before
   * the first measure. A MEASUREMENT and not a preference — never persisted,
   * and recomputed whenever the list changes — and the seam's ceiling:
   * `sidebarBounds` stops the pane just past the longest name on screen
   * (D104, follow-up). In the store rather than the pane because App clamps
   * the stored width with it, and the pane's box and the camera's inset must
   * be one number.
   */
  sidebarFit: number | null
  /** Projects folded away under their headings in the sidebar, by label —
   *  `core/projects.ts` holds the rules. Persisted in `Prefs`, as taste. */
  foldedProjects: string[]
  /** When each exploration was last opened on this device, by id — what the
   *  mark beside a row is measured against, so that it is an unread mark
   *  (D103, D334). `core/unread.ts` holds the rules. Persisted in `Prefs`, as
   *  taste. */
  openedAt: Record<string, number>
  rightTab: RightTab
  /**
   * The reader's own tab, held aside while a copy opened from a link and still
   * read first (`SharedCopy.readFirst`) has the pane on its Overview; null
   * otherwise. `restoreSession` sets it on the way into such a copy and hands it
   * back on the way into anything else, and the prefs save it in place of the
   * copy's tab, so a link never changes where the reader's own explorations open
   * — this session or after a reload. A tab the reader presses is theirs, and
   * ends the hold (`setRightTab`); so does a new exploration, which opens on the
   * thread.
   */
  readerTab: RightTab | null
  paletteOpen: boolean
  /** Which mode the setup sheet is in, or null when it is closed. One field and
   *  not two booleans: two booleans can both be true, and then the Escape ladder
   *  has to name a state that should not exist. */
  setupOpen: SetupMode | null
  /** Which group of controls the settings sheet is showing. A field on the same
   *  sheet and not a second sheet, for the reason `setupOpen` is one field: the
   *  Escape ladder names ONE open setup surface, and a section is a place within
   *  it rather than a state of its own. Meaningless while `setupOpen` is not
   *  `'settings'`, and deliberately not cleared on close — nothing reads it then. */
  setupSection: SettingsSection
  /**
   * The house whose key a pass was just refused for, while the settings sheet is
   * open because of it — so that card says why and takes the caret, rather than
   * the sheet opening on whichever provider happens to be first. Set only by
   * `askForKey`, cleared whenever the sheet closes.
   *
   * A MISSING key is asked for the same way as a rejected one. It used to open
   * Providers on no card in particular, on the grounds that the section already
   * said "No key for …" — but that line names the SELECTION's house, and with a
   * routed role the key a pass needed is often another's: the reader landed on
   * the first card and had to work out which field the pass wanted.
   */
  keyAsk: KeyAsk | null
  /** Whether the splash has ever closed. Persisted in `Prefs`, so the first screen
   *  is the first screen once. */
  splashSeen: boolean
  /**
   * When the open exploration was last written to disk, or null before the first
   * write of this session.
   *
   * Written by the autosave and read by the Library sheet. It exists
   * because an autosave nobody can see is indistinguishable from no autosave: this
   * app has always saved every change and has never once said so.
   */
  savedAt: number | null
  saveState: 'idle' | 'hydrating' | 'pending' | 'saving' | 'saved' | 'failed'
  saveError: string | null
  /** The library sheet, which lists every stored exploration. */
  libraryOpen: boolean
  /** The search over the stored explorations, on a sheet — the magnifying
   *  glass at the head of the sidebar and on its rail (D102). Not persisted,
   *  for the reason the keyboard reference below is not. */
  searchOpen: boolean
  /** The export sheet, which offers the eight formats. */
  exportOpen: boolean
  /** Which stored exploration the sheet is for, or null for the open one. The
   *  sidebar's per-row share must not switch what is open as a side effect of
   *  pressing a glyph, and `RecentEntry` carries no node data, so the sheet
   *  reads the stored document itself. */
  exportSessionId: string | null
  /** A specific row another surface asked Export to reveal, or null for the
   *  sheet's ordinary top-to-bottom entry. This is transient navigation state,
   *  not part of the exploration being exported. */
  exportSection: ExportSection | null
  /** The keyboard reference. Not persisted: a help sheet that reopened itself on
   *  the next load would be a sheet nobody asked for twice. */
  shortcutsOpen: boolean
  /** The whole prompt, from the seed caption's "Show more". Not persisted, for
   *  the reason the reference above is not. */
  promptOpen: boolean
  sourcesOpen: boolean
  researchOpen: boolean
  researchTab: ResearchTab
  /**
   * When a check finds matching passages in the user's own documents, read only
   * those and skip the web.
   *
   * A workspace preference rather than a per-node control: three tabs on every node,
   * one of them usually disabled, for a decision the retrieval already makes better.
   */
  preferCorpus: boolean
  /**
   * Which data sources are turned on, by id.
   *
   * SPARSE, and for the same reason `roleModels` is: an id this record does not
   * mention takes the registry's own `defaultOn`, so a row added in a later build
   * arrives with the default its author chose rather than off-by-omission for
   * everyone who ever opened the panel. The registry is NOT imported here — the
   * store keeps a raw record of what was actually chosen, and the surface that
   * knows the rows is the one that fills the gaps.
   */
  sourcesOn: Record<string, boolean>
  /**
   * A contact address sent to the polite pools some sources offer, empty for none.
   *
   * NOT a secret, and worth saying twice: it goes out as a `mailto=` query
   * parameter, it identifies a caller rather than authorising one, and it must
   * never be filed with the keys or rendered behind a password field.
   */
  sourceMailto: string
  /**
   * The name on the sidebar's profile row, empty for none.
   *
   * Kept in `nv.profile.name` beside the keys rather than in `nv.prefs`: a prefs
   * blob that fails to parse is discarded whole, which for a lens is a taste
   * reset and for a name the user typed is a deletion nobody announced.
   */
  profileName: string
  /**
   * Projects somebody has made and not yet put anything in, newest first.
   *
   * Every other group in the sidebar is DERIVED from the labels on the stored
   * rows, which is the right model and has one hole in it: a project with no
   * members is a label nothing carries. `core/projects.ts` holds the rules,
   * including the one that keeps this from becoming a second source of truth —
   * a label leaves the moment a member arrives.
   */
  declaredProjects: string[]
  /**
   * A seed offered to the idea box by something other than typing, or null.
   *
   * One-shot: the rail takes it, puts it in the draft, and clears it. It is not "the
   * seed" and it is not persisted — the seed of record is `session.seed`, written only
   * by `startSession`, and a proposal that outlived a reload would put words in
   * somebody's box that they had already declined once.
   *
   * A store field rather than a prop because the two ends are a modal dialog and a
   * rail that unmounts when collapsed, with no component above both of them holding
   * draft state. Nothing is spent by accepting one: it fills the box, and Explore is
   * still a separate press.
   */
  proposedSeed: string | null
  /**
   * A question handed to the Ask panel by the composer, or null. One-shot for
   * the same reason `proposedSeed` is: the panel takes it, sends it, and clears
   * it, and one that outlived that would re-ask itself on the next pin. A store
   * field because the two ends — the composer and a panel that unmounts with
   * the inspector — have nothing above both of them holding draft state.
   */
  proposedQuestion: string | null

  /**
   * A run that is happening, or whose card is still on screen. NEVER persisted:
   * a run is a thing in flight, and `Session.runs` is the thing that is kept.
   *
   * A flat projection rather than the loop's own state, because the loop's state is
   * Maps of Sets and this is read by React on every change.
   */
  run: RunLive | null
  /** What the cue layer has been told, for the open exploration. Never persisted,
   *  and for a reason of its own — see `coach.ts`. The cues themselves are derived
   *  on demand and live nowhere. */
  coach: CoachState | null
  /**
   * The spending ceiling for the NEXT run, as typed, empty for none.
   *
   * In the store rather than in the rail that renders it, for two reasons that are
   * both about the same failure. The rail is mounted conditionally — collapsing it
   * unmounts the component — so a ceiling held in component state would be silently
   * discarded by a keystroke that hides a panel, and the next press would spend
   * without the limit its owner believed they had set. And the command palette can
   * start a run too: while this lived in the rail, that path could not see it.
   *
   * A string because the intermediate states of typing a number are not numbers;
   * parsed once, when a run starts. Deliberately NOT persisted to `nv.prefs`: a
   * ceiling restored silently three weeks later would be applied to a run nobody
   * remembered setting it for, and both ways that goes wrong are worse than
   * retyping it.
   */
  runCeiling: string
  /** See `RunTune`. Read by the composer's "Let it run" and written by the tune
   *  panel and by commands committed from the box. */
  runTune: RunTune
  /** See `TuneDefaults`. Seeds `runTune` at boot; read by the surface that edits
   *  it and, through `seededTune`, by the composer's committed chips as their
   *  baseline. Changing the default still does NOT retune a run already
   *  configured, because the counts on screen are the ones about to be spent —
   *  the chips only re-measure which of those counts are worth a pill. */
  tuneDefaults: TuneDefaults
  /** The tune panel over the composer — lens, temper, filters and the run budget.
   *  A store flag rather than component state because the palette's Customize row
   *  and the composer's mode chip both open it, and neither is above the other. */
  tuneOpen: boolean
  /**
   * How many pixels of each stage edge are under floating chrome: the sidebar
   * and the thread column on the left (App), and the bottom band — the floating
   * composer, or the phone's node sheet — measured off the DOM by
   * `canvas/fieldInset.ts`. The panes float over the canvas so their glass has
   * something real to blur; the canvas itself spans the full stage, and this is
   * what keeps the camera honest about it. `frame`, `follow`, `centerOn` and
   * `settleLook` centre in the remaining visible field (`core/framing.ts`)
   * rather than under the glass.
   *
   * The spatial camera does not consult it: its projection centre is the canvas
   * centre by construction, so a 3D fit sits slightly off the visible centre
   * while a pane is open. Orbit is one drag away, and that bias is cheaper than
   * teaching the projection an asymmetric frustum.
   */
  viewInset: Inset
  /**
   * What the Ask tab is pinned to, or null.
   *
   * The conversation itself is NOT here and is not persisted — see `chatTurns`
   * below and the note in `chat.ts`. This field is only "which thing is the
   * question about".
   */
  chat: ChatTarget | null

  busy: boolean
  /**
   * How to stop the pass holding `busy`, when stopping it costs nothing — and
   * null for every pass that spends.
   *
   * The worked example's unfurl holds the lock for twenty seconds so that its
   * nodes arrive one beat at a time, and it spends nothing, so a door that would
   * otherwise refuse under `busy` stops it instead (`yieldUnpaid`). A paid pass
   * never registers one: its refusal is what stops a second pass being billed
   * over the first, and a live run is refused at each door before this is asked.
   * Set by `beginOp` for a `spends: false` op that hands one over, and cleared
   * everywhere the lock itself is released.
   */
  interrupt: (() => void) | null
  /**
   * Up next: presses that met the lock, each waiting to run once when the pass
   * in flight lands — or back as a chip that asks again. See `core/upNext.ts`
   * for the guardrail, and `ui/upNextPress.ts` for the one path in and out.
   *
   * Never persisted, and scoped to the exploration each was pressed in: a press
   * is a decision made in front of a live pass, and one that outlived a reload
   * or a switch would spend on a graph nobody was looking at.
   */
  upNext: UpNextItem[]
  thought: string
  /**
   * What the current pass is searching for, or opening, right now.
   *
   * A second line under the thoughtstream rather than part of `thought`: the
   * trace is the model's reasoning and this is an action it took, and splicing
   * one into the other would make the retained trace claim to contain something
   * the model never wrote.
   */
  searching: string | null
  /**
   * Every line `searching` has held since the pass began, oldest first — the
   * search trail the live pass lists (`core/searchTrail.ts`). Appended by
   * `setSearching` and cleared wherever `searching` is, so it can never outlive
   * the pass whose steps it names.
   */
  searchTrail: string[]
  /**
   * The line a pass carries while the transport waits to ask again — a rate
   * limit, an overload, a connection that did not answer — or null. Its own
   * channel and not `searching`, because where it stands depends on the trace:
   * before any thought has arrived it takes the placeholder's place ("Reading the
   * branch from the root down…" read on through a wait the API had asked for,
   * over a request nobody was reading); after, it takes the search line's.
   * Cleared wherever `searching` is.
   */
  waiting: string | null
  activeOp: OpKind | null
  activeTargets: string[]

  playhead: number | null
  /** When the playhead last moved — replay derives each node's growth from it. */
  playheadAt: number
  playing: boolean
  /** What the last breakthrough scan read, per session id. See `ScanNote`. */
  lastScan: Record<string, ScanNote>
  /**
   * The move list a plan pass proposed and nobody has yet approved or discarded.
   *
   * Not persisted, on the `lastScan` rule: it is a proposal about the graph as it
   * stood, and a list restored three weeks later would name nodes that may be
   * gone and spend a budget nobody remembers asking for. Approving it copies it
   * onto the run's own record (`Run.programme`), which IS persisted.
   */
  programmeDraft: ProgrammeDraft | null
  /**
   * One-shot signals that open the two FREE forms on the selected node — the
   * record-a-result form and the rewrite editor. Monotonic counters rather than
   * booleans: the listener reacts to the CHANGE, so two presses in a row both
   * land, and nothing has to be reset. Bumped by the keyboard (R, E) and by the
   * inspector's FREE rows; never persisted.
   */
  sigRecord: number
  sigRewrite: number
  pokeRecord: () => void
  pokeRewrite: () => void
  /**
   * The third one-shot, same contract: unfold the thread's selection strip so
   * the selected node's full reading — the whole claim, and every act against
   * it — is on screen. It opens no form, which is exactly why it exists apart
   * from the two above: the scrubber's card names a node whose claim it clamps
   * at three lines, and the press that answers "show me all of it" must not
   * also open an editor nobody asked for. Never persisted.
   */
  sigInspect: number
  pokeInspect: () => void
  /**
   * Another one-shot, same contract: show the explorations pane — open it where
   * it is collapsed, or as the phone's overlay — with focus on the row of the
   * exploration the thread is reading. Bumped by the thread's breadcrumb, whose
   * "Explorations" is the level above the title it leads to (D362). The pane's
   * phone overlay is its own local state, so a counter is the door in. Never
   * persisted.
   */
  sigExplorations: number
  pokeExplorations: () => void
  /**
   * The fourth one-shot, same contract: seat the caret in the composer's box.
   * Bumped by the doors that leave a person about to type — a new exploration's
   * empty canvas, and a key just saved — because focus left on the page body
   * there hands the first letter of their question to the graph's single-key
   * verbs. The composer takes it on desktop only; on a phone a programmatic
   * focus raises the keyboard over whatever the tap just opened. Never
   * persisted.
   */
  sigCaret: number
  pokeCaret: () => void
  /**
   * The fifth one-shot, same contract: open the name field on the OPEN
   * exploration's row in the sidebar — the palette's Rename row, which has no
   * field of its own and must not grow one (a second place to type a name is a
   * second set of rules for it). The sidebar owns the pane, the row and the
   * field; this only asks. Never persisted.
   */
  sigNaming: number
  pokeNaming: () => void
  /**
   * A one-shot ask to open the Breakthroughs tab with the across scope entered
   * — the pick unfolded, nothing spent. Set by the palette's across entry,
   * which used to run the default read directly: a row that cannot show the
   * pick must not be the door that spends past it. `runFocusId`'s contract and
   * not `sigRecord`'s, deliberately: the poke MOUNTS the surface it arms, and
   * a counter read at mount is a signal the mount has already seen — the
   * component consumes this on the way in and clears it, so reopening the tab
   * later never re-fires. The value is the SESSION ID the poke was made on,
   * because the flag can outlive its moment — the consumer drops an ask made
   * on any other graph rather than entering a scope nobody asked for there.
   * Never persisted.
   */
  scanAcrossAsk: string | null
  pokeScanAcross: () => void
  clearScanAcross: () => void
  /**
   * A one-shot ask to open the Library with its calibration ledger in view —
   * the "See the ledger" press under a recorded result. `scanAcrossAsk`'s
   * contract: the poke opens the sheet it arms, the sheet scrolls to the table
   * and clears the flag on the way in, so a later open lands at the top as it
   * always has. Never persisted.
   */
  ledgerAsk: boolean
  pokeLedger: () => void
  clearLedgerAsk: () => void

  /**
   * A run the reader asked to read in full — set by the "Read deeper" line's
   * run-account link, which names the node's OWN producing run rather than
   * opening the report on whatever ran last. RunReport consumes it on the way in
   * (scrolls to that run and lights it) and clears it, so it is a one-shot
   * landing: reopening the tab later never re-fires. View-only, never persisted,
   * and never part of the undo snapshot.
   */
  runFocusId: string | null

  /** Growth speed multiplier. Higher is faster. */
  tempo: number

  toasts: Toast[]

  /** Steps back and steps forward. Never persisted — history is this session's. */
  past: UndoEntry[]
  future: UndoEntry[]

  // ── actions ──────────────────────────────────────────────────────────────
  startSession: (seed: string, frame?: Frame, brief?: Directive[]) => string
  /**
   * Replace the standing brief. The ONE writer, like `addSpend`: directives
   * steer every subsequent pass, so two writers would let the prompt a pass
   * builds depend on which path last touched the list.
   */
  setBrief: (brief: Directive[]) => void
  /**
   * File the open exploration under a project, or pass null to unfile it. The
   * ONE writer, like `setBrief`: the label is what the sidebar groups on, and two
   * writers would let the group a row lands in depend on which path last touched
   * it. Normalised through `projectLabel`, so a label of spaces unfiles rather
   * than opening a group with a blank header. `history: false` is a project's
   * rename, which relabels without a step of history (D368).
   */
  setProject: (project: string | null, opts?: { history?: boolean }) => void
  /**
   * Name the open exploration, or pass null (or the seed's own words) to take the
   * name away, so the rows read the seed again. The ONE writer for the open one,
   * like `setProject`, and for its reasons — one step of history, so a name typed
   * into the wrong row comes back with ⌘Z. `nameStored` in `autosave.ts` is the
   * other door, for a document that is not open. Normalised through `nameFor`.
   *
   * The seed is never touched: a name is a label over it (`Session.name`), and
   * everything a pass reads is exactly what it was before.
   */
  setName: (name: string | null) => void
  /**
   * Install an empty session. `project`, when given, is ON the new session from
   * its first transition: the autosave reads a session change under the SAME id
   * as an edit and marks the save pending, then skips the flush because an
   * empty document is never written — so a label added in a second step would
   * leave "Saving changes…" standing until the seed landed. One transition,
   * with a new id, is the checkpoint path, which never marks anything pending.
   */
  clearSession: (project?: string | null) => void
  /**
   * The one door to a fresh canvas. The sidebar's button, the palette's row and
   * ⌘N all call this, so the three cannot drift into different locks again
   * — ⌘N once blocked on a FINISHED run's card, alone among them. Returns
   * whether it cleared, so a caller can close its own chrome only on success.
   *
   * `project` files the fresh canvas AT BIRTH — the + on a project heading in
   * the sidebar. Handed to `clearSession` rather than written through
   * `setProject`, for two reasons: that door takes a step of history and there
   * is nothing here to step back to — ⌘Z would offer "Undo Project" over an
   * empty canvas and answer with the same empty canvas, unfiled — and a second
   * write to the session after the clear is exactly the transition the autosave
   * mistakes for an edit (see `clearSession`). Clamped through `projectLabel`,
   * the ONE normalisation, so a project born here and one a fork mints are the
   * same string and therefore the same group. `startSession` carries the label
   * into the seeded exploration — see there.
   */
  newExploration: (project?: string | null) => boolean
  /**
   * Whether nothing holds the pass lock now — after stopping the pass that held
   * it, if that pass registered an `interrupt`. Every door that yields to the
   * worked example rather than refusing asks this, in place of reading `busy`.
   *
   * Synchronous, and the settle is part of the stop: the interrupt ends its op
   * on the document it began on before returning, so a caller that replaces the
   * session on the next line checkpoints a finished op, and nothing the stopped
   * unfurl owned acts after it. `newExploration` has to answer at once — the
   * sidebar's Forget clears through it and only then deletes, and that order is
   * the whole of its correctness — so a settle that had to be awaited could not
   * serve the one door this exists for.
   */
  yieldUnpaid: () => boolean
  /** Take a press behind the lock. The caller has asked `upNextRefusal`; the
   *  cap is held here as well, so no path can queue a third. */
  queuePress: (item: UpNextItem) => void
  unqueuePress: (id: string) => void
  /** Send a waiting press back as a chip that asks again, at today's figure. */
  holdPress: (id: string, reason: string, est: number | null) => void
  clearUpNext: () => void
  /** Install a stored exploration, replacing whatever is open. */
  restoreSession: (doc: StoredSession) => void
  /** Take a step of history, labelled with the verb about to run. */
  mark: (label: string) => void
  /**
   * Fork the exploration at a step of the replay: a NEW session carrying steps
   * 0…i, switched to live. The original stays stored exactly as it was.
   *
   * What the fork carries, and why: the nodes through step i (insertion order —
   * parents precede children, so the cut is closed over `parentId`; a bridge
   * parent beyond the cut is simply a strand not drawn, the same fact a prune
   * leaves); the bridges whose ends all survive; the ops whose produced nodes
   * all survive — a pass that produced a dropped step describes work the fork
   * does not carry. The runs and the spend ledger travel WHOLE: both are
   * records of what happened on the way here, and a fork's figure leaning past
   * its own steps is the one direction every figure in this app may err.
   * History does not cross the fork — the new document starts with none.
   */
  forkAtStep: (i: number) => boolean
  /**
   * Copy the open exploration WHOLE — every node, op, bridge, run and ledger row
   * — under a new id, switched to live. The original stays stored exactly as it
   * was: the autosave's id-change checkpoint writes the outgoing document.
   *
   * A fork at the last step would carry the same graph, and this is still a
   * different act worth its own door: `forkAtStep` is "take this branch
   * somewhere else", this is "keep this one and try something on a copy", and
   * a door that has to be spelled as "fork at the end" is a door nobody finds.
   *
   * Nothing is rebuilt on the way across. Verifications, panels and model stamps
   * travel by reference, so neither the `refuted` gate nor a provenance stamp is
   * re-decided here — the two invariants a copy path is most able to break.
   * Holds the same lock `newExploration` does and returns whether it copied, so
   * a caller can close its own chrome only on success.
   */
  duplicateSession: () => boolean
  /** Withdraw the last step if the action it was taken for changed nothing. */
  dropMark: (label: string) => void
  undo: () => void
  redo: () => void
  addNodes: (nodes: ReasonNode[]) => void
  patchNode: (id: string, patch: Partial<ReasonNode>) => void
  dropSubtree: (id: string) => number
  /** Fold a settled subtree away, or unfold it. */
  toggleCollapse: (id: string) => void
  /** Park a node further out along its own spoke, or nearer in. World units,
   *  bounded by `clampStretch`; zero puts it back where the layout wants it. */
  stretchNode: (id: string, stretch: number) => void
  expandAll: () => void
  /** Unfold every ancestor of a node, so anything that jumps to it can reach it. */
  revealNode: (id: string) => void
  addBridges: (b: Bridge[]) => void
  /** Record a field sweep on the session, the oldest dropped past the history. */
  addSweep: (sweep: FieldSweep) => void
  dropBridge: (id: string) => void
  /** Attach what a link scan found, or the synthesis a realisation wrote. Never
   *  reorders the list: the order a reader sees is computed where the list is
   *  rendered — see `addBridges`. */
  patchBridge: (id: string, patch: Partial<Bridge>) => void
  /**
   * Record what a pass cost, against the model that billed it.
   *
   * Merges on `spendKey` and appends on first use, so the ledger is one row per
   * model in first-use order. The ONE writer: a second one would make the figure
   * a user checks against a provider's dashboard depend on which path they took.
   *
   * `opId` attributes the same row to the pass that drew it — see `Op.spend`.
   * Passed by every call site that has an op in hand; the one that has none is
   * the Ask channel, whose draw is deliberately in the ledger and on no step.
   */
  /**
   * Record a receipt against the OPEN exploration's ledger.
   *
   * Deliberately only that one. A receipt for an exploration the reader has
   * since left belongs in that exploration's document, and writing a document
   * can evict — which needs the archive, which this file does not know about.
   * `recordSpendOn` in `autosave.ts` is where that goes. See D38.
   */
  addSpend: (s: Spend, opId?: string) => void
  /** Append one exact physical-call receipt to its logical operation. */
  appendModelCallReceipt: (opId: string, receipt: ModelCallReceipt) => void
  setAuditCoverage: (opId: string, coverage: import('./types').AuditCoverage) => void
  /** Note what a breakthrough scan ran over, so the scan control can say whether
   *  running it again would read anything new. See `lastScan`. */
  noteScan: (sessionId: string, rec: ScanNote) => void

  /**
   * Pin the Ask tab to something, and open the rail on it.
   *
   * One action rather than three calls at each site: every entry point wants the
   * same three effects, and a site that forgot the rail would look broken.
   */
  askAbout: (t: ChatTarget | null) => void

  setRun: (r: RunLive | null) => void
  setWeighting: (w: Weighting) => void
  setInstruments: (v: boolean) => void
  setShowRings: (v: boolean) => void
  setDimUnrelated: (v: boolean) => void
  setShowMinimap: (v: boolean) => void
  /** Hold or clear the proposed list. Approval is `startRun`'s, not the store's. */
  setProgrammeDraft: (p: Programme | null) => void
  /** Open the cue layer over the run that just closed, carrying whatever this
   *  session has already been told to stop showing. The one door the coach opens
   *  by itself; a pass — the seed's opening included — opens nothing, and the
   *  coach's count waits on a quiet line instead (`CoachLine`). */
  beginCoach: (runId: string | null) => void
  /** The Guide me path: reopen what is held, or read the graph as it stands.
   *  Returns false while a pass or a move owns attention. */
  openCoach: () => boolean
  /** The quiet line's press: the digest list alone — no card, no camera, no bar.
   *  Refuses exactly where `openCoach` does. */
  openCoachDigest: () => boolean
  /** The soft close: the card and the digest, and nothing else. */
  dismissCoach: () => void
  /** Re-point a held coach at the ending that just happened, without opening
   *  anything — the seam for runs that finish outside `beginCoach`'s gate. */
  retargetCoach: (runId: string | null) => void
  /** The bar's own ✕. The chip that brings it back is `openCoach`. */
  closeCoachBar: () => void
  setCoachIndex: (index: number) => void
  setCoachLens: (lens: CueLens) => void
  toggleCoachDigest: () => void
  toggleCoachWhy: () => void
  /** Answer one cue instance. `skipped` twice on a kind silences the kind. */
  retireCue: (key: string, kind: CueKind, how: 'acted' | 'skipped') => void
  muteCueKind: (kind: CueKind, key?: string) => void
  /** Everything retired in this session comes back. */
  unmuteCues: () => void
  setRunCeiling: (v: string) => void
  setRunTune: (t: RunTune) => void
  setTune: (open: boolean) => void
  setViewInset: (left: number, right: number) => void
  /** The bottom band's height, as measured. No `rev` bump — see the action. */
  setViewBottom: (bottom: number) => void
  /** Append the record at the START of a run, so a tab that dies mid-run leaves
   *  behind what it had already done — the same rule as `Op.finishedAt`. */
  beginRun: (r: Run) => void
  /** Patch a record in place. The one writer, so an account can never be assembled
   *  from two sources that disagree. */
  patchRun: (id: string, p: Partial<Run>) => void

  beginOp: (
    kind: OpKind,
    targets: string[],
    /** `model` overrides the stamp for a pass that does NOT run on the selected
     *  model — `audit` is the only one, and it exists precisely to run somewhere
     *  else. Without it the ledger would credit the audit to the model whose work
     *  was being attacked. `interrupt` is honoured only beside `spends: false`
     *  — see the field of that name. */
    opts?: {
      spends?: boolean
      model?: ModelStamp
      portfolio?: PortfolioRecord
      interrupt?: () => void
    },
  ) => string
  /**
   * Keep a streaming portfolio's durable account in lockstep with admitted
   * nodes. A pagehide between streamed groups must not save paid nodes detached
   * from their producing pass.
   */
  patchPortfolioOp: (opId: string, produced: string[], portfolio: PortfolioRecord) => void
  endOp: (
    opId: string,
    produced: string[],
    done?: {
      error?: string
      /** What that error was, typed. `run.ts` reads this rather than matching
       *  English against `error` — see `Op.errorKind` and `endsTheRun`. */
      errorKind?: Op['errorKind']
      /** The model declined. Not an error and not an empty result — see `Op`. */
      refused?: boolean
      refusalNote?: string
      /** Which safety filter declined it — see `Op.refusalCategory`. */
      refusalCategory?: string
      thinking?: string
      readExplorations?: number
      /** Physical portfolio-pass structure, written once when that pass settles. */
      portfolio?: PortfolioRecord
      /** The response model, when it differs from the model requested at start. */
      model?: ModelStamp
      /** A conjecture pass's admission tally — see `Op.admission`. */
      admission?: ConjectureAdmission
    },
  ) => void
  setThought: (t: string) => void
  setSearching: (t: string | null) => void
  setWaiting: (t: string | null) => void

  select: (id: string | null) => void
  hover: (id: string | null) => void
  setFocus: (id: string | null) => void

  setView: (v: Viewport, byUser?: boolean) => void
  /** Frame-by-frame camera easing. Never pins, never re-targets. */
  setViewMode: (m: '2d' | '3d', w?: number, h?: number) => void
  /** Orbit, in radians. Yaw is unbounded; pitch is clamped short of vertical. */
  orbitCam: (dYaw: number, dPitch: number) => void
  /** Multiply the eye distance. */
  dollyCam: (factor: number) => void
  /** Slide the look-at point across the ground plane, in screen axes. */
  panCam: (dx: number, dy: number, w: number) => void
  /** Raise or lower the look-at point along the evidence axis. */
  raiseCam: (dy: number) => void
  /** Frame the whole graph, keeping the angle you are standing at. */
  frameCam: (w: number, h: number) => void
  /** Ease toward a vantage point. Cleared by any direct manipulation. */
  flyCam: (c: Cam) => void
  /** One easing step, from the render loop. */
  applyCam: (c: Cam, arrived: boolean) => void
  /**
   * Orbit around this node from now on.
   *
   * The single most valuable gesture in an orbit rig, because orbiting a graph
   * about a point you are not looking at is what makes 3D viewers feel broken.
   */
  pivotOn: (id: string, byUser?: boolean) => void
  applyView: (v: Viewport, arrived: boolean) => void
  unpinView: () => void
  nudgeView: (dx: number, dy: number) => void
  zoomBy: (factor: number, cx?: number, cy?: number) => void
  /**
   * Fit: frame the whole graph (or `target`) into the visible field, and hand the
   * view back to follow. One press does both, from the tool dock, the graph
   * footer's zoom figure (D369), the palette, the F key or a double-click on empty
   * canvas — outside a run it is the one way back to follow, since Follow again
   * lives in the live run strip (invariant 11, D277).
   */
  frame: (w: number, h: number, target?: Bounds) => void
  /** Keep the growing graph framed, and frame the opening once it lands
   *  (`frameOpening`). A no-op while the view is pinned. */
  follow: (w: number, h: number) => void
  centerOn: (id: string, w: number, h: number, k?: number, byUser?: boolean) => void
  /** Ask the camera to bring a node into view — see `viewAsk`. */
  lookAt: (id: string) => void
  /** Answer `viewAsk` for a `w` × `h` stage, from the render loop. */
  settleLook: (w: number, h: number) => void

  toggleFilter: (k: keyof Filters) => void
  /** Every colour switch back on in one write, signal flow and labels left alone
   *  (`withChannelsOn`, D335). */
  showAllChannels: () => void
  /** Turn on exactly the switches hiding a node or its ancestors, then unfold
   *  and ask the camera (`revealNode`). A reader's press, never a landing's. */
  showNode: (id: string) => void
  setTheme: (t: Theme) => void
  setLens: (l: Lens) => void
  setTemper: (t: Temper) => void
  /** Set how hard one kind of work thinks, or pass null to clear it back to its
   *  default. Written through synchronously, for the reason `setModel` is: this
   *  decides what the next pass draws. */
  setEffortLevel: (work: EffortWork, level: Effort | null) => void
  setModel: (id: string) => void
  /** Point one role at a model, or pass null to clear it back to the selection. */
  setRoleModel: (role: Role, id: string | null) => void
  /** One provider's key; `null` removes it. Takes the provider EXPLICITLY and
   *  never infers it from `modelId` — the entire failure this area is built
   *  against is a key landing in the slot the user was not looking at. */
  setKey: (p: ProviderId, k: string | null) => void
  /** The endpoint and the model name for the custom provider. Written together
   *  because a URL with no model name calls nothing useful. */
  setCustom: (base: string, model: string, dialect?: 'responses' | 'chat') => void
  /** The counts a new exploration starts from. Written through synchronously, for
   *  the reason `setModel` is: this decides what the next pass does. */
  setTuneDefaults: (t: TuneDefaults) => void
  /** One source row's preference. Records only what was chosen — see `sourcesOn`. */
  setSourceOn: (id: string, on: boolean) => void
  /** The polite-pool contact address. An address, never a key. */
  setSourceMailto: (v: string) => void
  /** The name on the profile row; an empty one removes it. Written synchronously
   *  to `nv.profile.name` — see the table there for why it is not a preference. */
  setProfileName: (v: string) => void

  /**
   * Make an empty project. Returns the label it was clamped to, or null when
   * there was no name in what was typed.
   *
   * The clamp is `projectLabel`, the same one `setProject`, `fileStored` and
   * both fork doors come through, so a project declared here and a project a
   * fork mints are the same string and therefore the same group.
   */
  declareProject: (raw: string) => string | null
  /** Take an empty project off the list. The ✕ on its heading, and nothing else
   *  — a group with members has no ✕, because removing it would be a question
   *  about the explorations in it rather than about the label. */
  undeclareProject: (label: string) => void
  /**
   * Drop every declared label the documents now carry, and write only if that
   * changed anything.
   *
   * Called from the sidebar with the labels on both shelves once it has read the
   * index. It is a READ that can write, which is unusual enough to say out loud:
   * the alternative is for every door that files an exploration to remember to
   * un-declare, and a rule implemented at each of the places it applies is the
   * shape this codebase keeps being bitten by. One rule, one caller, one place.
   */
  syncProjects: (inUse: ReadonlySet<string>) => void

  setRail: (open: boolean) => void
  /** Collapse the explorations sidebar to its rail, or open it again. */
  setSidebarCollapsed: (v: boolean) => void
  /**
   * The thread column's width, or null to go back to following the stage.
   *
   * Stored RAW, unclamped: the bounds are a fact about the window the pane is on
   * and the same setting is read on several of them. Clamping into the slot
   * would quietly rewrite somebody's width the first time they opened a laptop,
   * and they would never get the big screen's column back.
   */
  setThreadWidth: (w: number | null) => void
  setSidebarWidth: (w: number | null) => void
  setSidebarFit: (px: number | null) => void
  /** Fold one project's rows away under its heading, or unfold them: the
   *  folder on the heading, pressed (D101). */
  toggleProjectFold: (label: string) => void
  /** Stamp one exploration as opened now. The autosave's switch hook calls it
   *  for every door that opens one; the rows call it for a press on the one
   *  already open; `beginRun` stamps inline. See `core/unread.ts`. */
  markOpened: (id: string) => void
  setRightTab: (t: RightTab) => void
  /** Open the run account on one specific run — scrolled into view and briefly
   *  lit — rather than on whatever ran last. Opens the rail and the Run tab like
   *  the other openers; RunReport still lists every run, so this is a landing,
   *  not a filter. */
  focusRun: (runId: string) => void
  /** RunReport calls this once it has landed on `runFocusId`, so reopening the
   *  tab later does not scroll to a run nobody just asked for. */
  clearRunFocus: () => void
  setPalette: (open: boolean) => void
  /** `section` is honoured only when opening the settings mode, and defaults to
   *  the providers rows — the splash has no sections to land on. */
  setSetup: (mode: SetupMode | null, section?: SettingsSection) => void
  /**
   * Open Settings → Providers on this provider's key, after a pass it served
   * failed on the key itself (`ModelError` kind `auth`: `'rejected'`, the
   * default) or was refused because there is none (`'missing'`, with what needed
   * it). The provider the refused call RESOLVED to — a routed role's, when the
   * role is routed — never the selection's by default: a check routed to another
   * house that is refused for that house's key must not send the reader to paste
   * the one they already have (invariant 4's other half).
   *
   * Only a house whose card the panel draws is singled out (`keyCardProviders`).
   * Any other — the judge on a build that names no endpoint (D107) — opens
   * Providers as before, on no card, rather than on a sentence about a field
   * that is not there. Opening the sheet calls nothing.
   */
  askForKey: (provider: ProviderId, ...ask: [] | ['rejected'] | ['missing', KeyNeed]) => void
  /**
   * `askForKey` for a pass refused because `resolveFor(role)` found no key: the
   * house and model the role resolves to — `entryFor`, the route the refusal read
   * — and what needed it, `what` or the role's own phrase. Never the selection's
   * house for a routed role, and never a fallback: it names the key the pass
   * would have used, and the pass still did not run.
   */
  askForMissingKey: (role: Role, what?: string) => void
  /**
   * `askForKey` for a pass that needs a key from ANOTHER company than `besides` —
   * a cross-examination, a blind round — when no such key is held: the first
   * hosted house the panel draws a card for that is not among `besides` and has
   * no key. Hosted only, because "add a key" is no advice about somebody's own
   * endpoint. With no such house the sheet opens on Providers and no card.
   */
  askForOtherKey: (besides: readonly ProviderId[], need: KeyNeed) => void
  setLibrary: (open: boolean) => void
  setSearchOpen: (open: boolean) => void
  /** Open the sheet for the OPEN exploration, or name a stored id to export THAT
   *  one without opening it. Closing always clears the target, so the next opener
   *  cannot inherit somebody else's document. */
  setExport: (open: boolean, sessionId?: string | null, section?: ExportSection | null) => void
  setShortcuts: (open: boolean) => void
  setPromptOpen: (open: boolean) => void
  setSources: (open: boolean) => void
  setResearchOpen: (open: boolean, tab?: ResearchTab) => void
  /** The single writer for the durable research workspace. */
  setResearchState: (research: ResearchState) => void
  setRunAssessments: (workspace: RunAssessmentWorkspace) => boolean
  /** Draft/execution data has no scientific-history or model-call side effect. */
  setComputationalWorkspace: (workspace: ComputationalWorkspaceState) => boolean
  setExperimentWorkspace: (workspace: ExperimentWorkspaceState) => boolean
  /** Apply a verified semantic merge/import as one undoable graph edit. */
  applySemanticSnapshot: (snapshot: SemanticSnapshot) => void
  setPreferCorpus: (v: boolean) => void
  /** Offer a seed to the idea box. Null clears it, which is what taking it does. */
  proposeSeed: (seed: string | null) => void
  proposeQuestion: (q: string | null) => void

  setPlayhead: (i: number | null) => void
  setPlaying: (v: boolean) => void
  setTempo: (t: number) => void

  toast: (text: string, tone?: Toast['tone'], action?: Toast['action']) => void
  dismissToast: (id: string) => void
}

// The dark field is the design's primary surface; light is a toggle, not a
// system inheritance. A stored choice still wins — and index.html's pre-paint
// script makes the same call, so first paint and boot agree.
const initialTheme: Theme = (readLS(LS.theme) as Theme | null) ?? 'dark'

/**
 * Read SYNCHRONOUSLY, at module evaluation.
 *
 * An async read here would boot with an empty session, the app's first-run
 * check would fire the demo, and the demo's `clearSession` would land on top of
 * the restore — both orderings of that race are wrong. The documents live in
 * IndexedDB even so: `main.tsx` awaits `hydrateStorage` and only then imports
 * this module, so by this line the mirror in `core/docStore.ts` is full and the
 * read below is a read of memory.
 */
// FIRST, before the key slots are read below: the lift has to have happened by
// the time anything asks whether this workspace has a key.
liftLegacyKey()
const booted = loadBootSession()
const prefs = loadPrefs()
// A tempo with no matching <option> renders the pace select blank, and
// `setTempo` clamps to a floor that is not one of the offered values.
const bootTempo = prefs && TEMPOS.some((t) => t.value === prefs.tempo) ? prefs.tempo : DEFAULT_TEMPO
/**
 * The stored selection, validated against the registry once.
 *
 * A model this build no longer offers falls back to the default rather than
 * standing: every capability question — can a quote be checked, what does effort
 * top out at — is answered from the entry, and an id with no entry would answer
 * them from `activeEntry`'s fallback while the topbar named the missing model.
 * The setup sheet says so in words when this fires.
 */
const storedModel = readLS(LS.model)
const bootModel = storedModel && findModel(storedModel) ? storedModel : DEFAULT_MODEL_ID
export const BOOT_MODEL_UNKNOWN = !!storedModel && !findModel(storedModel)

/**
 * What a profile chosen before D313 meant, as levels. Thorough is the defaults.
 * Standard lowered routine moves under Grounded and Balanced to medium and Lean
 * lowered every move, so both become Reasoning at Medium; Lean also brought the
 * scan for bridges down to High. Standard's held Challenge has no level of its
 * own to keep, and a workspace that chose to spend less keeps spending less.
 */
const LEGACY_PROFILE_LEVELS: Readonly<Record<string, EffortLevels>> = {
  'routine-derive-medium': { reason: 'medium' },
  lean: { reason: 'medium', scan: 'high' },
}

/**
 * The stored levels, read entry by entry and never trusted as a whole — JSON from
 * localStorage, so absent, malformed, an array, or an object whose keys name a
 * kind of work this build does not have. A bad entry is dropped and that kind
 * thinks at its default; unlike a missing model there is no capability question
 * it would answer wrongly. With nothing stored under the new key, a profile the
 * person chose before D313 is read in its place, so a boot never raises a level
 * somebody had lowered. Pure, and handed both stored strings, so the migration is
 * testable without booting a second store.
 */
export function readEffortLevels(raw: string | null, legacyProfile: string | null): EffortLevels {
  if (raw === null)
    return legacyProfile !== null && Object.hasOwn(LEGACY_PROFILE_LEVELS, legacyProfile)
      ? { ...LEGACY_PROFILE_LEVELS[legacyProfile] }
      : {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  const out: EffortLevels = {}
  for (const work of EFFORT_WORK) {
    const level = (parsed as Record<string, unknown>)[work]
    if (isEffort(level)) out[work] = level
  }
  return out
}

/**
 * The stored routing, validated the same way and with the same tolerance.
 *
 * Every layer of this is defensive on purpose, because the value is JSON from
 * localStorage: it may be absent, malformed, an array, or an object whose values
 * are numbers. A role naming a model this build no longer offers is DROPPED
 * rather than repaired to the default — dropping means that role falls back to
 * the selection, which is the documented meaning of an absent entry, whereas
 * repairing would invent a routing decision the user never made and pin it.
 */
function bootRoles(): Partial<Record<Role, string>> {
  const raw = readLS(LS.roles)
  if (!raw) return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  const out: Partial<Record<Role, string>> = {}
  for (const role of ROLES) {
    const id = (parsed as Record<string, unknown>)[role]
    if (typeof id === 'string' && findModel(id)) out[role] = id
  }
  return out
}

/**
 * The stored run defaults, read field by field and never trusted as a whole.
 *
 * Defensive at every layer for the reason `bootRoles` is — this is JSON from
 * localStorage, so it may be absent, malformed, an array, or an object whose
 * `moves` is a string. A field that fails is DROPPED rather than repaired, and a
 * dropped field means "whatever this build ships", which is the state of a
 * workspace that never opened the tune panel. Repairing would invent a number
 * nobody chose and then spend against it.
 *
 * `'every'` is the one non-numeric value a check count is allowed to hold (see
 * `RunTune`); the string "3" is not that value and is dropped like any other.
 */
function bootTuneDefaults(): TuneDefaults {
  const raw = readLS(LS.tune)
  if (!raw) return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  const o = parsed as Record<string, unknown>
  const count = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
  const out: TuneDefaults = {}
  const moves = count(o.moves)
  if (moves !== undefined) out.moves = moves
  if (o.checks === 'every') out.checks = 'every'
  else {
    const checks = count(o.checks)
    if (checks !== undefined) out.checks = checks
  }
  const panels = count(o.panels)
  if (panels !== undefined) out.panels = panels
  const audits = count(o.audits)
  if (audits !== undefined) out.audits = audits
  if (o.execution === 'sequential' || o.execution === 'portfolio') out.execution = o.execution
  if (o.detail === 'full' || o.detail === 'progressive') out.detail = o.detail
  if (o.mode === 'auto' || o.mode === 'confirm') out.mode = o.mode
  return out
}
const bootTune = bootTuneDefaults()

/**
 * The stored source preferences, kept sparse.
 *
 * Only booleans survive, and an id whose value is anything else is dropped
 * rather than coerced — dropping puts that row back on the registry's own
 * `defaultOn`, which is the honest answer to "this workspace never said".
 */
function bootSourcesOn(): Record<string, boolean> {
  const raw = readLS(LS.sourcesOn)
  if (!raw) return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  const out: Record<string, boolean> = {}
  for (const [id, on] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof on === 'boolean') out[id] = on
  }
  return out
}

/**
 * What a fork family is called when its source was never filed.
 *
 * The source's own seed, clamped by the one label clamp, or a standing label
 * when there is no seed to borrow — a bare canvas has none, and a group header
 * reading as nothing at all is worse than a plain one. Exported because there
 * are two doors and they must agree: `duplicateSession` names the open session's
 * family, `duplicateStored` names a stored document's, and a second copy of this
 * sentence is how the same fork comes to land in two different groups.
 */
export function forkFamilyLabel(seed: string): string {
  return projectLabel(seed) ?? 'Untitled exploration'
}

/**
 * Research records travel across a fork as provenance, while live analyses that
 * name claims outside a step cut do not. The full evidence/audit ledgers stay:
 * like spend and archived checks, they report what happened on the way here and
 * are allowed to lean past the materialised graph, never to invent a result.
 */
function researchForFork(
  research: ResearchState | undefined,
  sessionId: string,
  keep?: ReadonlySet<string>,
): ResearchState {
  const now = Date.now()
  const state = research ?? createResearchState({ id: `research_${sessionId}`, now })
  const normalized = normalizeResearchState({
    ...state,
    id: `research_${sessionId}`,
    updatedAt: Math.max(state.updatedAt, now),
    claimScopes: keep ? state.claimScopes.filter((row) => keep.has(row.nodeId)) : state.claimScopes,
    contradictionAnalyses: keep
      ? state.contradictionAnalyses.filter(
          (row) => keep.has(row.left.nodeId) && keep.has(row.right.nodeId),
        )
      : state.contradictionAnalyses,
    falsificationPortfolios: keep
      ? state.falsificationPortfolios.filter((row) => keep.has(row.targetId))
      : state.falsificationPortfolios,
    calibrationTournaments: keep
      ? state.calibrationTournaments.filter((row) => keep.has(row.targetId))
      : state.calibrationTournaments,
  })
  return keep ? pruneManualEvidenceBindingsForNodes(normalized, keep) : normalized
}

/**
 * The field sweeps a branch carries: each one whose node it carries, and each
 * one a carried conjecture anchors to. Not named on the fork's literal, a sweep
 * was a field the fork silently dropped — and with it every anchor that named
 * one of its items.
 */
function sweepsForBranch(
  sweeps: readonly FieldSweep[] | undefined,
  nodes: Readonly<Record<string, ReasonNode>>,
): { sweeps?: FieldSweep[] } {
  const anchored = new Set(Object.values(nodes).flatMap((n) => n.conjecture?.anchors ?? []))
  const kept = (sweeps ?? []).filter(
    (w) => nodes[w.nodeId] !== undefined || w.items.some((i) => anchored.has(i.id)),
  )
  return kept.length ? { sweeps: kept } : {}
}

/**
 * How far the flat view zooms by hand — a press, the wheel, a pinch — through
 * `zoomBy`. Exported for the footer's zoom presses, which stand down at either
 * end rather than offer a press that does nothing (D369).
 */
export const ZOOM_MIN = 0.12
export const ZOOM_MAX = 4

/**
 * The camera a flat Fit would choose for bounds `b` on a `w`×`h` stage. One
 * function so the seed caption, which is drawn at its set size at a Fit's zoom
 * (`seedScale` in `canvas/seedCaption.ts`), reads the same zoom `frame` sets.
 */
export function fitCamera(b: Bounds, w: number, h: number, inset: Inset): Viewport {
  return fitView(b, fitField(visibleField(w, h, inset), FIT_EDGE), 0.12, 1.5)
}

export const useStore = create<State>((set, get) => ({
  session: booted?.session ?? emptySession(),
  bounds: booted
    ? layout(booted.session.nodes, booted.session.order)
    : { minX: -600, minY: -400, maxX: 600, maxY: 400 },
  rev: 0,

  selectedId: booted?.ui.selectedId ?? null,
  hoverId: null,
  focusId: booted?.ui.focusId ?? null,

  view: { x: 0, y: 0, k: 1 },
  viewMode: prefs?.viewMode ?? '2d',
  weighting: prefs?.weighting ?? 'model',
  // Two defaults in one expression, and `loadPrefs` decides between them: an
  // existing workspace keeps its instruments, a fresh one starts calm.
  instruments: prefs?.instruments ?? false,
  showRings: prefs?.showRings ?? true,
  dimUnrelated: prefs?.dimUnrelated ?? false,
  showMinimap: prefs?.showMinimap ?? true,
  cam: CAM_HOME,
  camTarget: null,
  viewTarget: null,
  viewPinned: false,
  viewAsk: null,
  frameOpening: false,
  filters: prefs?.filters ?? DEFAULT_FILTERS,

  theme: initialTheme,
  lens: booted?.ui.lens ?? prefs?.lens ?? 'first-principles',
  temper: booted?.ui.temper ?? prefs?.temper ?? 'balanced',
  effortLevels: readEffortLevels(readLS(LS.effortLevels), readLS(LS.effort)),
  modelId: bootModel,
  roleModels: bootRoles(),
  keys: {
    anthropic: readLS(LS.key('anthropic')),
    openai: readLS(LS.key('openai')),
    // A custom endpoint may legitimately need no key at all — a model on this
    // machine does not — so null here is a normal resting state rather than a
    // missing setting, and `canCall` treats it as callable when the base URL is set.
    google: readLS(LS.key('google')),
    custom: readLS(LS.key('custom')),
    // The judge's key. Null is the ordinary state and turns nothing off: a run
    // orders its checks by structure alone and a check records no second reading,
    // and every surface that would have printed one says the judge was not asked.
    typesafe: readLS(LS.key('typesafe')),
  },
  customBase: readLS(LS.customBase) ?? '',
  customModel: readLS(LS.customModel) ?? '',
  customDialect: readLS(LS.customDialect) === 'chat' ? 'chat' : 'responses',

  railRight: prefs?.railRight ?? true,
  sidebarCollapsed: prefs?.sidebarCollapsed ?? false,
  threadWidth: prefs?.threadWidth ?? null,
  sidebarWidth: prefs?.sidebarWidth ?? null,
  sidebarFit: null,
  foldedProjects: prefs?.foldedProjects ?? [],
  openedAt: prefs?.openedAt ?? {},
  rightTab: prefs?.rightTab ?? 'node',
  readerTab: null,
  paletteOpen: false,
  setupOpen: null,
  setupSection: 'providers',
  keyAsk: null,
  splashSeen: prefs?.splashSeen ?? false,
  // Null rather than the stored document's `savedAt`: what this field reports is
  // "this app has written since you opened it", and claiming a write that happened
  // in a previous session would make the indicator a lie on the one load where a
  // user is most likely to be checking it.
  savedAt: null,
  saveState: 'idle',
  saveError: null,
  libraryOpen: false,
  searchOpen: false,
  exportOpen: false,
  exportSessionId: null,
  exportSection: null,
  shortcutsOpen: false,
  promptOpen: false,
  sourcesOpen: false,
  researchOpen: false,
  researchTab: 'findings',
  preferCorpus: prefs?.preferCorpus ?? false,
  sourcesOn: bootSourcesOn(),
  sourceMailto: readLS(LS.sourceMailto) ?? '',
  profileName: readLS(LS.profileName) ?? '',
  declaredProjects: parseDeclared(readLS(LS.projects)),
  proposedSeed: null,
  proposedQuestion: null,

  run: null,
  coach: null,
  // Not reset by a session change, unlike `run` itself. A live run belongs to the
  // exploration it is threading; a ceiling for the next one is a control setting
  // like the tempo or the temper, and clearing it when someone opens another graph
  // would quietly remove a limit they set two minutes ago.
  runCeiling: '',
  // Same rule as the ceiling: a control setting, not a property of the open
  // exploration, so a session change leaves it alone. Seeded from `nv.tune` where
  // that names a field, and from the shipped numbers where it does not — the two
  // are indistinguishable by design, so a workspace with no stored defaults boots
  // exactly as it did before they existed.
  runTune: seededTune(bootTune),
  tuneDefaults: bootTune,
  tuneOpen: false,
  viewInset: { left: 0, right: 0, bottom: 0 },
  chat: null,
  busy: false,
  interrupt: null,
  upNext: [],
  thought: '',
  searching: null,
  searchTrail: [],
  waiting: null,
  activeOp: null,
  activeTargets: [],

  playhead: null,
  playheadAt: 0,
  playing: false,
  lastScan: {},
  programmeDraft: null,
  sigRecord: 0,
  sigRewrite: 0,
  sigInspect: 0,
  sigExplorations: 0,
  sigCaret: 0,
  sigNaming: 0,
  scanAcrossAsk: null,
  ledgerAsk: false,
  runFocusId: null,

  tempo: bootTempo,

  toasts: [],
  past: [],
  future: [],

  // ── session ──────────────────────────────────────────────────────────────

  startSession: (seed, frame, brief) => {
    motion.reset()
    pulses.reset()
    const before = get().session
    const s = emptySession(seed.trim())
    // Stored only when it means something: absent IS `claim`, and normalising here
    // keeps a document written under the default indistinguishable from one
    // written before frames existed — which is what it is.
    if (frame && frame !== 'claim') s.frame = frame
    // Dated as they join, and capped as the codec caps them — see `setBrief`.
    if (brief?.length) s.brief = stampDirectives(brief.slice(0, MAX_BRIEF), Date.now())
    // A BLANK canvas's filing is a decision made before the seed — the + on a
    // project heading files the fresh canvas at birth (`newExploration`), and the
    // seed is the first thing written into that place, not a replacement for it.
    // The empty canvas is never written to disk (`autosave.ts` skips a session
    // with no nodes), so this is the only way that label reaches a document.
    // BLANK only: a seed typed over a graph that had nodes replaces that
    // exploration, and its label goes with the rest of it, as it always has.
    const born = before.order.length === 0 ? projectLabel(before.project) : undefined
    if (born) s.project = born
    const root = makeNode({
      label: seed.trim(),
      claim: seed.trim(),
      reasoning: '',
      kind: 'seed',
      confidence: 0.5,
      novelty: 0.5,
      tension: 0,
      origin: 'seed',
      parentId: null,
      createdOrdinal: 0,
    })
    s.nodes[root.id] = root
    s.order.push(root.id)
    s.nextNodeOrdinal = 1
    const bounds = layout(s.nodes, s.order)
    set({
      session: s,
      bounds,
      rev: get().rev + 1,
      selectedId: root.id,
      focusId: null,
      viewPinned: false,
      viewTarget: null,
      viewAsk: null,
      frameOpening: false,
      playhead: null,
      playing: false,
      thought: '',
      searching: null,
      searchTrail: [],
      waiting: null,
      // A run belongs to the exploration it ran on. Left standing, its pod and its
      // counts follow the user into a different graph, and the ending's press ("See
      // the account", or "What it found") lands on a tab that has never had a run.
      // A pinned question is the same case: its node id means nothing in another
      // document.
      run: null,
      // Retirement is scoped to the exploration it was taken in: what somebody
      // decided not to be shown about this graph says nothing about the next one.
      coach: null,
      chat: null,
      // A new exploration opens on the thread, where its first pass is about to
      // stream. The tab is a stored preference, so without this a reader who
      // last left the Overview open met a brand-new question on a brief with
      // nothing in it yet. Only the doors that make a NEW exploration say this:
      // reopening one (`restoreSession`) keeps the reader's tab.
      rightTab: 'node',
      readerTab: null,
      // Per the field's own contract: null before the first write of THIS
      // exploration. Carried over, the capsule would call a new graph saved on
      // the strength of a write that belonged to the one it replaced.
      savedAt: null,
      // A stack spanning two sessions would let a step back resurrect an
      // exploration the user deliberately replaced.
      past: [],
      future: [],
    })
    return root.id
  },

  restoreSession: (doc) => {
    // Not optional: `motion` is a module singleton keyed by node id, and the
    // demo's ids are literals — so opening a stored demo while another demo's
    // eased positions are cached would tween every node across the canvas.
    motion.reset()
    pulses.reset()
    const s = doc.session
    const bounds = layout(s.nodes, s.order)
    // The tab: the reader's, as D168 has it, except that a copy read first opens
    // on its Overview — the reading its sender would have exported — with the
    // reader's own tab held aside until they leave it (`readerTab`).
    const held = get().readerTab ?? get().rightTab
    const readFirst = s.shared?.readFirst === true
    set({
      session: s,
      bounds,
      rev: get().rev + 1,
      rightTab: readFirst ? 'breakthroughs' : held,
      readerTab: readFirst ? held : null,
      selectedId:
        doc.ui.selectedId && s.nodes[doc.ui.selectedId] ? doc.ui.selectedId : (s.order[0] ?? null),
      hoverId: null,
      // A stale focus id yields a one-element focus set, which dims the entire
      // canvas to nothing.
      focusId: doc.ui.focusId && s.nodes[doc.ui.focusId] ? doc.ui.focusId : null,
      // The session's own lens and temper win over the current preference: an
      // exploration reasoned adversarially reopens adversarially.
      lens: doc.ui.lens,
      temper: doc.ui.temper,
      view: { x: 0, y: 0, k: 1 },
      viewTarget: null,
      viewPinned: false,
      viewAsk: null,
      frameOpening: false,
      playhead: null,
      playing: false,
      busy: false,
      interrupt: null,
      thought: '',
      searching: null,
      searchTrail: [],
      waiting: null,
      // See `startSession`: a run belongs to the exploration it ran on, and so
      // does a question about one of its nodes — and so does a write timestamp.
      run: null,
      // And so does a coach's retirement: see `startSession`.
      coach: null,
      chat: null,
      savedAt: null,
      activeOp: null,
      activeTargets: [],
      past: [],
      future: [],
    })
  },

  mark: (label) => {
    const st = get()
    set({ past: [...st.past.slice(-(UNDO_DEPTH - 1)), snapshot(st, label)], future: [] })
    const sessionId = st.session.id
    // The mutation guarded by `mark` is synchronous on every manual path. A
    // microtask sees its settled semantic board; paid asynchronous paths also
    // append at `endOp`, after their result actually exists.
    queueMicrotask(() => {
      const live = get()
      if (live.session.id !== sessionId) return
      set({ session: withKnowledgeRevision(live.session, label) })
    })
  },

  /**
   * Withdraw the last step, when the action it was taken for changed nothing.
   *
   * A refusal, an empty result or a network error all leave the graph exactly as
   * it was, and a step back that restores an identical graph — while announcing
   * that it undid something — is worse than no step at all.
   */
  dropMark: (label) => {
    const st = get()
    const last = st.past[st.past.length - 1]
    if (!last || last.label !== label) return
    set({ past: st.past.slice(0, -1) })
  },

  undo: () => {
    const st = get()
    // An un-ended run holds the history shut — the same door `newExploration`
    // holds, for the same class of reason: the loop's own counters, closed
    // sets and attempt maps do not rewind with the graph, so a step back
    // under a live run leaves it working a document that no longer matches —
    // and an undo at a confirm gate spends the run's one history mark before
    // the run has finished writing under it. A FINISHED run's card is a
    // report and holds nothing. The run's own header scopes its "releasing
    // the lock keeps undo alive" rationale to this door. See D61.
    if (st.run !== null && st.run.phase !== 'ended') {
      st.toast('One thought at a time — let the current branch finish.', 'warn')
      return
    }
    const entry = st.past[st.past.length - 1]
    if (!entry) {
      st.toast('Nothing to undo.', 'info')
      return
    }
    set({
      past: st.past.slice(0, -1),
      future: [...st.future.slice(-(UNDO_DEPTH - 1)), snapshot(st, entry.label)],
    })
    restore(set, get, entry)
    const restored = get()
    set({ session: withKnowledgeRevision(restored.session, `Undo: ${entry.label}`) })
    get().toast(`Undid ${entry.label}.`, 'info')
  },

  redo: () => {
    const st = get()
    // The same door `undo` holds, in the other direction: a step re-applied
    // under a live run changes the graph beneath the loop just the same.
    if (st.run !== null && st.run.phase !== 'ended') {
      st.toast('One thought at a time — let the current branch finish.', 'warn')
      return
    }
    const entry = st.future[st.future.length - 1]
    if (!entry) {
      st.toast('Nothing to redo.', 'info')
      return
    }
    set({
      future: st.future.slice(0, -1),
      past: [...st.past.slice(-(UNDO_DEPTH - 1)), snapshot(st, entry.label)],
    })
    restore(set, get, entry)
    const restored = get()
    set({ session: withKnowledgeRevision(restored.session, `Redo: ${entry.label}`) })
    get().toast(`Redid ${entry.label} — the same nodes, not a new call.`, 'info')
  },

  setBrief: (next) => {
    const st = get()
    // One step of history: removing a directive changes what every later pass is
    // told, and that is exactly the kind of change undo exists for.
    st.mark('Brief')
    // A directive this list did not already hold is dated as it joins; one it
    // held keeps what it had, including no date at all — a directive stored
    // before `at` existed must not be dated to whichever edit touched the list
    // next. Capped as the codec caps it, so what every pass is sent from here is
    // what a reload reads back (the writers refuse first, and say why:
    // `briefRefusal`). Words a pass was briefed with and that leave — removed, or
    // reworded in place — are kept, dated, beside it. All of it is `briefWrite`.
    const { brief, former } = briefWrite(st.session, next, Date.now())
    set({
      session: {
        ...st.session,
        ...(brief.length ? { brief } : { brief: undefined }),
        ...(former.length ? { formerDirectives: former } : { formerDirectives: undefined }),
      },
      rev: st.rev + 1,
    })
  },

  setProject: (project, opts) => {
    const st = get()
    // One step of history, like the brief: filing an exploration moves it in the
    // sidebar, and a label typed into the wrong row should come back with ⌘Z
    // rather than by remembering what it used to say. Not for a project's RENAME
    // (D368): that relabels every member, and only this one is in the store, so
    // ⌘Z would file the open exploration back under a name nothing else wears.
    if (opts?.history !== false) st.mark('Project')
    const label = projectLabel(project)
    set({
      session: { ...st.session, ...(label ? { project: label } : { project: undefined }) },
      rev: st.rev + 1,
    })
  },

  setName: (raw) => {
    const st = get()
    const name = nameFor(raw, st.session.seed)
    // Asked for what it already is: not a step of history, and not a write. A
    // field opened and closed again without a change would otherwise leave an
    // "Undo Name" that undoes nothing.
    if (name === nameFor(st.session.name, st.session.seed)) return
    st.mark('Name')
    // The seed is not in this literal on purpose: only the label moves.
    set({
      session: { ...st.session, ...(name ? { name } : { name: undefined }) },
      rev: st.rev + 1,
    })
  },

  newExploration: (project = null) => {
    const s = get()
    // A pass in flight or a LIVE run holds the lock; a finished run's card is
    // a report, not a running thing, and does not. The worked example's unfurl
    // spends nothing, so it is stopped rather than waited for — the run is
    // asked first, so nothing is stopped for a press that is then refused.
    if ((s.run !== null && s.run.phase !== 'ended') || !s.yieldUnpaid()) {
      get().toast('One thought at a time — let the current branch finish.', 'warn')
      return false
    }
    // Born filed, in the clear's own transition. A label of spaces is no label,
    // exactly as it is at every other door: `projectLabel` answers undefined
    // and the canvas stays unfiled.
    get().clearSession(project)
    // Every door ends here — the sidebar's row and its rail, ⌘N, the palette —
    // so the caret lands in the empty box once, for all of them.
    get().pokeCaret()
    return true
  },

  yieldUnpaid: () => {
    const st = get()
    if (!st.busy) return true
    if (!st.interrupt) return false
    st.interrupt()
    return !get().busy
  },

  queuePress: (item) => {
    // A new press of a verb whose chip is asking again IS the answer to that
    // chip, so it replaces it rather than standing beside it.
    const kept = get().upNext.filter(
      (i) => !(i.held && i.verb === item.verb && i.nodeId === item.nodeId),
    )
    if (upNextHere({ session: get().session, upNext: kept }).length >= UP_NEXT_MAX) return
    set({ upNext: [...kept, item] })
  },
  unqueuePress: (id) => set({ upNext: get().upNext.filter((i) => i.id !== id) }),
  holdPress: (id, reason, est) =>
    set({ upNext: get().upNext.map((i) => (i.id === id ? { ...i, held: reason, est } : i)) }),
  clearUpNext: () => {
    if (get().upNext.length) set({ upNext: [] })
  },

  clearSession: (project = null) => {
    motion.reset()
    pulses.reset()
    const s = emptySession()
    const label = projectLabel(project)
    if (label) s.project = label
    set({
      session: s,
      bounds: { minX: -600, minY: -400, maxX: 600, maxY: 400 },
      rev: get().rev + 1,
      selectedId: null,
      hoverId: null,
      focusId: null,
      playhead: null,
      playing: false,
      view: { x: 0, y: 0, k: 1 },
      viewTarget: null,
      viewPinned: false,
      viewAsk: null,
      frameOpening: false,
      // The live-pass fields, cleared here as they are everywhere else that
      // installs a session. Left behind, they would leave a stale trace, a stale
      // search line and a stale run pod under an empty canvas.
      thought: '',
      searching: null,
      searchTrail: [],
      waiting: null,
      run: null,
      // See `startSession`: retirement belongs to the exploration it was taken in.
      coach: null,
      chat: null,
      // See `startSession`: a new exploration opens on the thread.
      rightTab: 'node',
      readerTab: null,
      savedAt: null,
      past: [],
      future: [],
    })
  },

  addNodes: (incoming) => {
    if (!incoming.length) return
    const s = get().session
    const nodes = { ...s.nodes }
    const order = s.order.slice()
    let nextNodeOrdinal =
      s.nextNodeOrdinal ??
      s.order.reduce((max, id, index) => Math.max(max, s.nodes[id]?.createdOrdinal ?? index), -1) +
        1
    const now = Date.now()
    for (const n of incoming) {
      if (nodes[n.id]) continue
      // Refuse to attach to a parent we don't have; better a dropped node
      // than a graph with a dangling edge.
      if (n.parentId !== null && !nodes[n.parentId]) continue
      const requestedOrdinal = n.createdOrdinal
      const createdOrdinal =
        requestedOrdinal !== undefined &&
        Number.isSafeInteger(requestedOrdinal) &&
        requestedOrdinal >= nextNodeOrdinal
          ? requestedOrdinal
          : nextNodeOrdinal
      nextNodeOrdinal = createdOrdinal + 1
      nodes[n.id] = {
        ...n,
        createdOrdinal,
      }
      order.push(n.id)
      // A branch that is growing right now is not a branch you have settled.
      // The whole ancestor chain, not just the parent: a fold anywhere above
      // would leave the drain threading nodes into an invisible subtree while
      // the user watches nothing happen after paying for it.
      let up = n.parentId
      const seen = new Set<string>()
      while (up && !seen.has(up)) {
        seen.add(up)
        const par = nodes[up]
        if (!par) break
        if (par.collapsed) nodes[up] = { ...par, collapsed: false, foldAt: now }
        up = par.parentId
      }
    }
    if (order.length === s.order.length) return
    const next: Session = { ...s, nodes, order, nextNodeOrdinal }
    const bounds = layout(next.nodes, next.order)
    set({
      session: next,
      bounds,
      rev: get().rev + 1,
      // The coach is derived rather than frozen, so a grown graph needs no reset —
      // it simply reads differently on the next pass. Only the card stands down.
      coach: calmedCoach(get().coach),
    })
  },

  patchNode: (id, patch) => {
    const s = get().session
    const cur = s.nodes[id]
    if (!cur) return
    const nodes = { ...s.nodes, [id]: { ...cur, ...patch } }
    const next: Session = { ...s, nodes }
    // Only geometry-affecting changes need a relayout, but relayout is cheap
    // and correctness beats a micro-optimisation here.
    const bounds = layout(next.nodes, next.order)
    set({
      session: next,
      bounds,
      rev: get().rev + 1,
      coach: calmedCoach(get().coach),
    })
  },

  dropSubtree: (id) => {
    const s = get().session
    if (!s.nodes[id] || s.nodes[id]!.parentId === null) return 0

    const childrenOf = new Map<string, string[]>()
    for (const nid of s.order) {
      const p = s.nodes[nid]?.parentId
      if (!p) continue
      const l = childrenOf.get(p)
      if (l) l.push(nid)
      else childrenOf.set(p, [nid])
    }
    const doomed = new Set<string>()
    const stack = [id]
    while (stack.length) {
      const cur = stack.pop()!
      if (doomed.has(cur)) continue
      doomed.add(cur)
      for (const c of childrenOf.get(cur) ?? []) stack.push(c)
    }

    const nodes: Record<string, ReasonNode> = {}
    const order: string[] = []
    for (const nid of s.order) {
      if (doomed.has(nid)) continue
      nodes[nid] = s.nodes[nid]!
      order.push(nid)
    }
    // A rebase remains the current repair only while at least one authored head
    // remains. Pruning the final replacement reopens the mechanical notice;
    // otherwise a saved document could not distinguish that user action from a
    // forged receipt naming outputs that never existed.
    for (const nid of order) {
      const node = nodes[nid]!
      if (!node.evidenceRebases) continue
      const evidenceRebases = node.evidenceRebases.filter((row) =>
        row.produced.some((producedId) => nodes[producedId] !== undefined),
      )
      if (evidenceRebases.length !== node.evidenceRebases.length) {
        nodes[nid] = {
          ...node,
          evidenceRebases: evidenceRebases.length ? evidenceRebases : undefined,
        }
      }
    }
    // Only local ends can be pruned; a foreign end belongs to another graph.
    const bridges = s.bridges.filter((b) => !bridgeTouches(b, doomed))
    const next: Session = {
      ...s,
      nodes,
      order,
      bridges,
      verificationArchive: archiveVerificationsFor(s, doomed),
    }
    const bounds = layout(next.nodes, next.order)
    const sel = get().selectedId
    set({
      session: next,
      bounds,
      rev: get().rev + 1,
      selectedId: sel && doomed.has(sel) ? (order[0] ?? null) : sel,
      focusId: get().focusId && doomed.has(get().focusId!) ? null : get().focusId,
      // Descendant counts changed even where every target survived, and the coach
      // is KEPT anyway: its cues recompute over the pool that survived, and the
      // retirement stays honest — a cue whose anchor was just pruned simply stops
      // firing, which is the correct outcome and not a reason to forget a mute.
      coach: calmedCoach(get().coach),
    })
    return doomed.size
  },

  toggleCollapse: (id) => {
    const s = get().session
    const cur = s.nodes[id]
    if (!cur || cur.parentId === null) return // folding the seed folds the app
    const kids = childrenIndex(s)
    const next = !cur.collapsed
    // Folding needs something to fold. *Unfolding* must always be possible —
    // a prune can leave a node collapsed with no children, and a fold nobody
    // can release is a trap.
    if (next && !kids.get(id)?.length) return
    // `collapsed: false` rather than deleting the key: `patchNode` merges and
    // cannot remove one, and false-plus-foldAt is what keeps the release
    // animation running.
    const nodes = { ...s.nodes, [id]: { ...cur, collapsed: next, foldAt: Date.now() } }
    const sess: Session = { ...s, nodes }
    const bounds = layout(sess.nodes, sess.order)

    const inside = new Set<string>()
    if (next) {
      const st = [...(kids.get(id) ?? [])]
      while (st.length) {
        const c = st.pop()!
        if (inside.has(c)) continue
        inside.add(c)
        for (const g of kids.get(c) ?? []) st.push(g)
      }
    }
    const sel = get().selectedId
    const foc = get().focusId
    set({
      session: sess,
      bounds,
      rev: get().rev + 1,
      // Selection and focus inside the fold have nowhere to live, so the stub
      // inherits them — the same hand-back `dropSubtree` does.
      selectedId: sel && inside.has(sel) ? id : sel,
      focusId: foc && inside.has(foc) ? id : foc,
      coach: calmedCoach(get().coach),
    })
  },

  stretchNode: (id, stretch) => {
    const s = get().session
    const cur = s.nodes[id]
    // The seed is the origin. It has no spoke, and moving it would move the
    // point every other position on the canvas is measured from.
    if (!cur || cur.parentId === null) return
    const next = clampStretch(stretch)
    if ((cur.stretch ?? 0) === next) return
    // `stretch: 0` rather than deleting the key, for the reason `collapsed` is
    // written false: `patchNode` merges and cannot remove one.
    const nodes = { ...s.nodes, [id]: { ...cur, stretch: next } }
    const sess: Session = { ...s, nodes }
    set({
      session: sess,
      bounds: layout(sess.nodes, sess.order),
      rev: get().rev + 1,
      // Pinned, on the same rule a pan or a zoom is: the reader has said where
      // they want something, and auto-follow would answer by reframing the whole
      // graph — zooming back out over the room the drag just bought. `f` and the
      // fit button still release it.
      viewPinned: true,
      viewTarget: null,
    })
  },

  expandAll: () => {
    const s = get().session
    const now = Date.now()
    const nodes = { ...s.nodes }
    let touched = false
    for (const id of s.order) {
      const n = nodes[id]
      if (!n?.collapsed) continue
      nodes[id] = { ...n, collapsed: false, foldAt: now }
      touched = true
    }
    if (!touched) return
    const sess: Session = { ...s, nodes }
    set({
      session: sess,
      bounds: layout(sess.nodes, sess.order),
      rev: get().rev + 1,
      coach: calmedCoach(get().coach),
    })
  },

  revealNode: (id) => {
    const s = get().session
    if (!s.nodes[id]) return
    const nodes = { ...s.nodes }
    let cur = nodes[id]?.parentId ?? null
    const guard = new Set<string>()
    let touched = false
    const now = Date.now()
    while (cur && !guard.has(cur)) {
      guard.add(cur)
      const n = nodes[cur]!
      if (n.collapsed) {
        nodes[cur] = { ...n, collapsed: false, foldAt: now }
        touched = true
      }
      cur = n.parentId
    }
    // Unfolded is half of reachable; the other half is on screen. Every caller
    // is a press that jumps to a node by name — a thread chip, a feed row, a
    // finding, the palette, a search hit — and a node picked by name is as
    // likely as not outside the field, so it asks the camera too. A request and
    // not a move: `settleLook` leaves a node already in view exactly where it
    // is. No pass landing calls this, which is what keeps it clear of the
    // `viewPinned` guardrail — a reader's pick may move a pinned view, a
    // landing never does.
    if (!touched) {
      set({ viewAsk: id })
      return
    }
    const sess: Session = { ...s, nodes }
    set({
      session: sess,
      bounds: layout(sess.nodes, sess.order),
      rev: get().rev + 1,
      viewAsk: id,
    })
  },

  // There is deliberately no `pulse` action any more. A pass touching a node is
  // animation, and it goes through `core/pulse.ts` — a clock beside `motion` —
  // rather than through the session, because a session write here re-rendered
  // every subscriber and rebuilt every strand for a 1.5 s glow. See that file.

  addBridges: (b) => {
    if (!b.length) return
    const s = get().session
    const seen = new Set(s.bridges.map(pairKey))
    const fresh = b.filter((x) => {
      const k = pairKey(x)
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
    if (!fresh.length) return
    // Appended, never sorted here. The order a reader sees is by rung, and a
    // rung is read off records that change under the list — a check landing
    // on a synthesis moves its candidate — so the tab sorts at render through
    // `sortBridges` and a stored order would only ever be stale.
    const bridges = [...s.bridges, ...fresh]
    set({
      session: { ...s, bridges },
      rev: get().rev + 1,
      coach: calmedCoach(get().coach),
    })
  },

  addSweep: (sweep) => {
    const s = get().session
    set({
      session: { ...s, sweeps: withSweep(s.sweeps, sweep) },
      rev: get().rev + 1,
      coach: calmedCoach(get().coach),
    })
  },

  dropBridge: (id) => {
    const s = get().session
    const bridges = s.bridges.filter((b) => b.id !== id)
    if (bridges.length === s.bridges.length) return
    set({
      session: { ...s, bridges },
      coach: calmedCoach(get().coach),
    })
  },

  patchBridge: (id, patch) => {
    const s = get().session
    const i = s.bridges.findIndex((b) => b.id === id)
    // A candidate the user dropped while a scan was in flight is gone, and the
    // scan's result has nowhere to land. Silent: the pass already billed, and a
    // toast about a card that is no longer on screen explains nothing.
    if (i < 0) return
    const bridges = s.bridges.slice()
    bridges[i] = { ...bridges[i]!, ...patch, id: bridges[i]!.id }
    set({
      session: { ...s, bridges },
      rev: get().rev + 1,
      coach: calmedCoach(get().coach),
    })
  },

  appendModelCallReceipt: (opId, receipt) => {
    const s = get().session
    const ops = s.ops.map((op) => {
      if (op.id !== opId) return op
      const prior = op.modelCalls ?? []
      // Physical-call order is append-only. A duplicate callback or a caller
      // trying to splice a later call into the middle is ignored rather than
      // making a durable multi-call receipt claim the wrong sequence.
      if (receipt.ordinal !== prior.length + 1 || prior.some((held) => held.id === receipt.id)) {
        return op
      }
      return { ...op, modelCalls: [...prior, receipt] }
    })
    set({ session: { ...s, ops } })
  },

  setAuditCoverage: (opId, coverage) => {
    const s = get().session
    set({
      session: {
        ...s,
        ops: s.ops.map((op) =>
          op.id === opId && op.kind === 'audit'
            ? { ...op, auditCoverage: structuredClone(coverage) }
            : op,
        ),
      },
    })
  },

  addSpend: (row, opId) => {
    // Nothing spent, nothing to record. See `spentNothing`.
    if (spentNothing(row)) return
    const s = get().session
    const spend = mergeSpend(s.spend, row)
    // The same write, attributed. The op's own row is what the step rail reads —
    // per-model rows cannot say which step drew what — and stamping it HERE, in
    // the one money writer, is what stops the two records ever disagreeing. A
    // multi-request pass (a panel's seats, a link scan's gathers) merges by
    // summing; one op runs on one model by construction, so the stamp is safe.
    const ops = opId
      ? s.ops.map((o) =>
          o.id === opId
            ? {
                ...o,
                // A first receipt starts the op's row knowing its largest request
                // (D311), as `mergeSpend` starts the model's.
                spend: o.spend ? addSpendRow(o.spend, row) : withPeak(row),
              }
            : o,
        )
      : s.ops
    set({ session: { ...s, spend, ops } })
  },

  noteScan: (sessionId, rec) => set({ lastScan: { ...get().lastScan, [sessionId]: rec } }),
  pokeRecord: () => set({ sigRecord: get().sigRecord + 1, railRight: true, rightTab: 'node' }),
  pokeRewrite: () => set({ sigRewrite: get().sigRewrite + 1, railRight: true, rightTab: 'node' }),
  pokeInspect: () => set({ sigInspect: get().sigInspect + 1, railRight: true, rightTab: 'node' }),
  pokeExplorations: () => set({ sigExplorations: get().sigExplorations + 1 }),
  pokeCaret: () => set({ sigCaret: get().sigCaret + 1 }),
  pokeNaming: () => set({ sigNaming: get().sigNaming + 1 }),
  pokeScanAcross: () =>
    set({ scanAcrossAsk: get().session.id, railRight: true, rightTab: 'breakthroughs' }),
  clearScanAcross: () => set({ scanAcrossAsk: null }),
  pokeLedger: () => set({ libraryOpen: true, ledgerAsk: true }),
  clearLedgerAsk: () => set({ ledgerAsk: false }),
  focusRun: (runId) => set({ runFocusId: runId, railRight: true, rightTab: 'run' }),
  clearRunFocus: () => set({ runFocusId: null }),

  forkAtStep: (i) => {
    const s = get().session
    if (i < 0 || i >= s.order.length) return false
    const carried = s.order.slice(0, i + 1)
    const keep = new Set(carried)
    // Ordinary passes cross the cut only whole. A rebase is deliberately
    // multi-headed and remains current while any replacement head survives, the
    // same rule pruning and persistence enforce; its receipt may therefore name
    // sibling heads outside this fork just as an old op may name a pruned node.
    const ops = s.ops.filter((o) =>
      o.kind === 'rebase'
        ? o.produced.some((id) => keep.has(id))
        : o.produced.every((id) => keep.has(id)),
    )
    const rebaseOps = new Set(ops.filter((op) => op.kind === 'rebase').map((op) => op.id))
    const nodes: typeof s.nodes = {}
    for (const id of carried) {
      const node = s.nodes[id]
      if (!node) continue
      const evidenceRebases = node.evidenceRebases?.filter((row) => rebaseOps.has(row.opId))
      nodes[id] = {
        ...node,
        ...(node.evidenceRebases
          ? { evidenceRebases: evidenceRebases?.length ? evidenceRebases : undefined }
          : {}),
      }
    }
    const forkId = uid('s')
    const at = Date.now()
    const fork: Session = {
      id: forkId,
      seed: s.seed,
      ...(s.frame ? { frame: s.frame } : {}),
      // Whole, with a gap's answer unlinked from a gap the cut leaves behind —
      // see `briefForBranch`.
      ...(s.brief ? { brief: briefForBranch(s.brief, keep) } : {}),
      // What the brief used to say travels whole, like the runs: a record of the
      // way here, which the branch's thread reads between the passes it carries.
      ...(s.formerDirectives ? { formerDirectives: s.formerDirectives } : {}),
      // A fork stays in its source's project — that is the whole reason the
      // sidebar groups by project rather than by date. Enumerated here like
      // every other field, so a session field that is not named on this literal
      // is a field the fork silently drops.
      ...(s.project ? { project: s.project } : {}),
      // The name travels for the project's reason: it is what the reader calls
      // this line of work, and a branch continues it. What tells the two apart is
      // the next field, printed under the name on the fork's row — and a name of
      // its own is one Rename away.
      ...(s.name ? { name: s.name } : {}),
      // Where it came from, counted from 1 as the rail counts: the step whose
      // card was pressed is the step the row says.
      forkedFrom: { from: s.id, kind: 'branch', step: i + 1, at },
      // A copy that came by link is still missing what the link left out, and a
      // branch of it is missing the same things — so the disclosure travels
      // (never the read-first mark: a branch is the reader's own work). The
      // receipts counted are at most the passes the branch keeps; the replay is
      // carried whole (`researchForFork`), so its count stands.
      ...(s.shared?.left
        ? {
            shared: {
              left: {
                ...s.shared.left,
                receipts: Math.min(s.shared.left.receipts, ops.length),
              },
            },
          }
        : {}),
      research: researchForFork(s.research, forkId, keep),
      createdAt: at,
      nodes,
      order: carried,
      bridges: s.bridges.filter((b) => bridgeReadable(b, nodes)),
      ops,
      // A sweep is reportage about one node, so it crosses the cut with that node
      // — and with any carried conjecture that anchors to one of its items, since
      // an anchor may name an item and not a node (D280, D290).
      ...sweepsForBranch(s.sweeps, nodes),
      nextNodeOrdinal: s.nextNodeOrdinal,
      // Step replay has no event-time axis for checks, and the full op/spend
      // ledgers already cross this fork. Checks on nodes outside the cut remain
      // in the audit archive rather than becoming receipts with no finding.
      verificationArchive: archiveVerificationsFor(
        s,
        s.order.filter((id) => !keep.has(id)),
      ),
      spend: s.spend.map((r) => ({ ...r })),
      runs: s.runs,
    }
    set({
      session: fork,
      selectedId: carried[carried.length - 1] ?? null,
      playhead: null,
      playing: false,
      focusId: null,
      past: [],
      future: [],
      rev: get().rev + 1,
      // A fork is a new exploration under a new id, and retirement is scoped to
      // the exploration it was taken in — the same rule every other session
      // boundary applies. Carried across, the old board's bar, pins and mutes
      // would reappear over a graph they were never about.
      coach: null,
    })
    return true
  },

  duplicateSession: () => {
    const st = get()
    // The same lock `newExploration` holds, and for its reason: this replaces
    // what is open, and a pass in flight is threading nodes into it. A FINISHED
    // run's card is a report and does not hold it.
    if (st.busy || (st.run !== null && st.run.phase !== 'ended')) {
      st.toast('One thought at a time — let the current branch finish.', 'warn')
      return false
    }
    // Both doors name a family the same way — see `duplicateStored`. A filed
    // source hands its label over; an UNFILED one is filed under its own seed, on
    // both sides, because a copy is the moment a lone exploration becomes a
    // family worth grouping. Left unfiled, the two would sit in the unfiled
    // bucket with nothing on screen saying they are related.
    const filed = projectLabel(st.session.project)
    const project = filed ?? forkFamilyLabel(st.session.seed)
    // Filed FIRST, and through the writer the sidebar uses. The label has to be
    // on the OUTGOING session object, because that object is what the autosave's
    // id-change checkpoint writes back to disk one `set` from here; a label
    // applied afterwards would be a label on a document nothing writes again.
    if (!filed) get().setProject(project)
    const s = get().session
    const copyId = uid('s')
    const at = Date.now()
    const copy: Session = {
      id: copyId,
      seed: s.seed,
      ...(s.frame ? { frame: s.frame } : {}),
      ...(s.brief ? { brief: s.brief } : {}),
      ...(s.formerDirectives ? { formerDirectives: s.formerDirectives } : {}),
      project,
      // Carried, and told apart by the origin under it — see `forkAtStep`.
      ...(s.name ? { name: s.name } : {}),
      forkedFrom: { from: s.id, kind: 'copy', at },
      // What a link left out is a fact about the document, so a copy of a
      // linked copy says it too — see `forkAtStep`. The read-first mark stays
      // behind: the copy is the reader's own work from the start.
      ...(s.shared?.left ? { shared: { left: s.shared.left } } : {}),
      research: researchForFork(s.research, copyId),
      createdAt: at,
      // Everything travels whole and BY REFERENCE. Nothing is rebuilt, so a
      // stored verification cannot be re-gated here (a second copy of the
      // `refuted` gate) and no node can be re-stamped with a model that did not
      // produce it. Sharing the records is safe because every writer in this
      // file replaces them rather than mutating in place — `order` is copied and
      // the ledger rows are spread for the same reason `forkAtStep` copies them.
      nodes: s.nodes,
      order: s.order.slice(),
      bridges: s.bridges,
      ops: s.ops,
      ...(s.sweeps ? { sweeps: s.sweeps } : {}),
      nextNodeOrdinal: s.nextNodeOrdinal,
      verificationArchive: s.verificationArchive,
      spend: s.spend.map((r) => ({ ...r })),
      runs: s.runs,
    }
    set({
      session: copy,
      // The replay's cursor and the history, cleared exactly as `forkAtStep`
      // clears them and for its reasons: a playhead indexes an order this is no
      // longer the only copy of, and a step back must not reach across a fork.
      playhead: null,
      playing: false,
      past: [],
      future: [],
      rev: get().rev + 1,
      // A copy is a new exploration under a new id, and retirement is scoped to
      // the exploration it was taken in.
      coach: null,
      // `selectedId` and `focusId` are deliberately NOT cleared, which is the one
      // place this differs from `forkAtStep`: the node ids are kept, so both
      // still resolve, and landing on the same node is what "a copy of this"
      // means. A cut carries no such promise, which is why the fork clears them.
    })
    return true
  },

  askAbout: (t) => {
    if (!t) {
      set({ chat: null })
      return
    }
    set({ chat: t, rightTab: 'ask', railRight: true })
  },

  setRun: (r) => set({ run: r }),
  setProgrammeDraft: (p) =>
    set({
      programmeDraft: p
        ? {
            programme: p,
            sessionId: get().session.id,
            claims: programmeClaimKey(get().session.nodes, p),
          }
        : null,
    }),

  beginCoach: (runId) => {
    const st = get()
    set({ coach: freshCoach(st.session.id, runId, st.coach) })
  },
  openCoach: () => {
    const st = get()
    if (coachDoorShut(st)) return false
    const held = st.coach
    // Always the newest ending, in both branches below. A held coach outlives the
    // runs that follow it (see `beginRun`), and a run that ends without opening one
    // — a Stop, an unreachable provider, a run that produced nothing — would
    // otherwise leave the door reopening onto the run before last. The seam names
    // the same run this does, so the two ways in cannot disagree.
    const source = latestFinishedRun(st.session)?.id ?? null
    // Reopened rather than rebuilt, so what this session was already told to stop
    // showing survives being put away and picked up again.
    if (held && held.sessionId === st.session.id) {
      set({ coach: { ...held, sourceRunId: source, open: true, digest: false, bar: true } })
      return true
    }
    // By hand, with nothing of this session held: the graph as it stands. A session
    // that never finished a run — the demo — reads fine, and the run-scoped kinds
    // simply do not fire.
    set({ coach: freshCoach(st.session.id, source, null) })
    return true
  },
  openCoachDigest: () => {
    const st = get()
    if (coachDoorShut(st)) return false
    const held = st.coach
    // The list, and nothing else: no card over the graph, no camera walk, and no
    // bar — the bar reports a run that ended, and this door is pressed on a graph
    // whether or not one did. A held coach keeps what it was told to stop showing
    // and the run it was speaking about; the line that opens this counted that
    // same held board, so the list is the count it promised.
    if (held && held.sessionId === st.session.id) {
      set({ coach: { ...held, digest: true, open: false, why: false } })
      return true
    }
    // Nothing held: the graph as it stands, with no run behind it — the reading
    // `CoachLine` counted (`restingCoach` in `ui/coachView.ts`), so the number
    // on the line and the rows under it cannot disagree.
    set({
      coach: { ...freshCoach(st.session.id, null, null), open: false, bar: false, digest: true },
    })
    return true
  },
  dismissCoach: () => {
    const c = get().coach
    const calmed = calmedCoach(c)
    if (calmed !== c) set({ coach: calmed })
  },
  retargetCoach: (runId) => {
    const c = get().coach
    // A held coach outlives runs, and not every ending passes through
    // `beginCoach` — a Stop, an unreachable provider, a run that produced
    // nothing. Left alone, `sourceRunId` would keep naming the run before last,
    // so the bar would print the WRONG stop head over the ending that just
    // happened and the run-scoped kinds would read a run nobody just watched.
    // The pointer moves; nothing opens and nothing retired is touched.
    if (c) set({ coach: { ...c, sourceRunId: runId } })
  },
  closeCoachBar: () => {
    const c = get().coach
    // The card goes with it: the bar is the surface that says a run ended, and
    // leaving a card up under a dismissed bar would be an ending that never closed.
    if (c) set({ coach: { ...c, open: false, digest: false, why: false, bar: false } })
  },
  setCoachIndex: (index) => {
    const c = get().coach
    if (!c) return
    // No upper clamp here: the visible list is derived and this state does not know
    // its length. The card reads past the end as nothing to show, which is what a
    // cue retired between the press and the render leaves behind anyway.
    set({ coach: { ...c, index: Math.max(0, Math.trunc(index)), why: false } })
  },
  setCoachLens: (lens) => {
    const c = get().coach
    // Back to the top: the order just changed, so position 3 of the old one names
    // a different cue than the one that was being read.
    if (c) set({ coach: { ...c, lens, index: 0 } })
  },
  toggleCoachDigest: () => {
    const c = get().coach
    if (!c) return
    set({
      coach: c.digest
        ? { ...c, digest: false, open: true }
        : { ...c, digest: true, open: false, why: false },
    })
  },
  toggleCoachWhy: () => {
    const c = get().coach
    if (c?.open) set({ coach: { ...c, why: !c.why } })
  },
  retireCue: (key, kind, how) => {
    const c = get().coach
    if (c) set({ coach: coachRetire(c, key, kind, how, Date.now()) })
  },
  muteCueKind: (kind, key) => {
    const c = get().coach
    if (c) set({ coach: coachMute(c, kind, Date.now(), key) })
  },
  unmuteCues: () => {
    const c = get().coach
    if (c) set({ coach: coachUnmute(c) })
  },
  setRunCeiling: (v) => set({ runCeiling: v }),
  setRunTune: (t) => set({ runTune: t }),
  setTune: (open) => set({ tuneOpen: open }),
  setViewInset: (left, right) => {
    const vi = get().viewInset
    if (vi.left === left && vi.right === right) return
    // The rev bump is what asks the canvas loop to re-follow: the host does not
    // resize when a pane toggles (that is the point of the panes floating), so
    // without it the camera would keep the old centring until the next
    // structural change. Eased like any other follow, so the graph breathes
    // sideways rather than snapping.
    set({ viewInset: { left, right, bottom: vi.bottom }, rev: get().rev + 1 })
  },
  setViewBottom: (bottom) => {
    const vi = get().viewInset
    const next = Math.max(0, Math.round(bottom))
    if (vi.bottom === next) return
    // NO rev bump, unlike the columns above. The band changes far more often —
    // the composer grows a line, the selected node's row appears in it, the
    // phone's sheet opens on every node tap — and `rev` rebuilds every strand's
    // geometry. The canvas loop re-follows on the inset object changing instead
    // (NeuralCanvas), which asks for exactly the one thing that moved.
    set({ viewInset: { ...vi, bottom: next } })
  },

  beginRun: (r) => {
    const s = get().session
    set({
      session: { ...s, runs: [...s.runs, r] },
      rev: get().rev + 1,
      // A run starts in the OPEN exploration, so whoever started it is looking
      // at it: stamped as opened here, so the run's finish has something to be
      // measured against later. For an exploration this device has never
      // stamped — one from before the mark existed — this is what lets its
      // dot fill for the first time (`core/unread.ts`, D334).
      openedAt: withOpened(get().openedAt, s.id, Date.now()) ?? get().openedAt,
      // Closed rather than cleared, and this is the only dismissal site where the
      // difference is load-bearing. Retirement is scoped to the EXPLORATION, not to
      // one run — but every run passes through here, so clearing would mean a mute
      // never outlived the run it was taken in, and the person would be told to stop
      // showing `prune` again after every single run. What the card was saying about
      // the previous graph goes away; what it was told stays.
      coach: calmedCoach(get().coach),
    })
  },

  patchRun: (id, p) => {
    const s = get().session
    const i = s.runs.findIndex((r) => r.id === id)
    if (i === -1) return
    const runs = s.runs.map((r, j) => (j === i ? { ...r, ...p } : r))
    set({ session: { ...s, runs }, rev: get().rev + 1 })
  },

  // ── operations ───────────────────────────────────────────────────────────

  beginOp: (kind, targets, opts) => {
    // A demo session about to spend real tokens stops being the demo and takes
    // its own id, so a later "load the demo" can never overwrite it. The demo's
    // own unfurl spends nothing, so it passes `spends: false` and the pinned id
    // survives — otherwise a key-connected demo would leak a new document on
    // every replay and never be stored as the demo at all.
    const spends = opts?.spends !== false
    const op: Op = {
      id: uid('op'),
      kind,
      targetIds: targets,
      startedAt: Date.now(),
      produced: [],
      // A pass that spends nothing ran on no model. The same flag, two facts —
      // which is why demo.ts needs no edit for provenance. The override is for
      // the one pass that deliberately runs off the selected model: without it
      // an audit's op row names the model whose work it was attacking, while the
      // spend row beside it — stamped from the response's own model — names the
      // auditor, and the two records disagree about who ran the same pass.
      ...(spends ? { model: opts?.model ?? stampOf(activeEntry(get())) } : {}),
      ...(opts?.portfolio ? { portfolio: opts.portfolio } : {}),
    }
    const s = get().session
    const id = spends && s.id === DEMO_SESSION_ID && canCall(get()) ? uid('s') : s.id
    // A live pass owns attention until it settles, so the card stands down. The
    // retirement is kept: a pass is not an answer to a cue. And when the line
    // above re-mints the demo's id, the held coach FOLLOWS the new name — the
    // remint renames the same document mid-gesture, nothing switched — because
    // left stamped with the demo id, the next `openCoach` would read it as
    // another exploration's and rebuild, dropping every mute this person just
    // taught it.
    const calmed = calmedCoach(get().coach)
    set({
      session: { ...s, id, ops: [...s.ops, op] },
      busy: true,
      interrupt: !spends ? (opts?.interrupt ?? null) : null,
      activeOp: kind,
      activeTargets: targets,
      thought: '',
      searching: null,
      searchTrail: [],
      waiting: null,
      coach: calmed && id !== s.id ? { ...calmed, sessionId: id } : calmed,
      // An opening the loop has not framed yet is not this pass's to frame.
      frameOpening: false,
    })
    // ...and everything OUTSIDE the store that keys state by exploration id
    // hears the same thing, instead of trying to infer it. The coach above is
    // kept inline because it is part of this one atomic write; a subscriber
    // cannot be. See `core/rename.ts` for why inferring it does not work.
    // A no-op unless the line above actually reminted, so no condition here.
    announceRename(s.id, id)
    return op.id
  },

  patchPortfolioOp: (opId, produced, portfolio) => {
    const s = get().session
    const ops = s.ops.map((o) =>
      o.id === opId && o.kind === 'portfolio' ? { ...o, produced, portfolio } : o,
    )
    set({ session: { ...s, ops } })
  },

  endOp: (opId, produced, done) => {
    const s = get().session
    const { thinking, clipped } = clipThinking(done?.thinking)
    const ops = s.ops.map((o) =>
      o.id === opId
        ? {
            ...o,
            finishedAt: Date.now(),
            produced,
            error: done?.error,
            errorKind: done?.errorKind,
            // Carried onto the op rather than only toasted: a refusal that reaches
            // the ledger as an empty result is indistinguishable from an exhausted
            // branch, and anything reading the ledger afterwards then reports the
            // model's safety layer as a fact about the idea.
            refused: done?.refused,
            refusalNote: done?.refusalNote,
            ...(done?.refusalCategory ? { refusalCategory: done.refusalCategory } : {}),
            thinking,
            thinkingClipped: clipped,
            readExplorations: done?.readExplorations,
            portfolio: done?.portfolio,
            model: done?.model ?? o.model,
            // Only a conjecture pass counts its admissions; absence elsewhere is
            // the information, not a zero.
            ...(o.kind === 'conjecture' && done?.admission ? { admission: done.admission } : {}),
          }
        : o,
    )
    const ended = ops.find((o) => o.id === opId)
    const finishedSession = withKnowledgeRevision(
      { ...s, ops },
      `${ended ? opLabel(ended, s) : 'Operation'} ${done?.error || done?.refused ? 'ended without a result' : 'completed'}`,
      {
        eventId: opId,
        ...(ended?.model ? { actor: { kind: 'model', model: ended.model, opId } } : {}),
      },
    )
    set({
      session: finishedSession,
      busy: false,
      interrupt: null,
      activeOp: null,
      activeTargets: [],
      thought: '',
      searching: null,
      searchTrail: [],
      waiting: null,
      // The opening landed: the flat loop's next follow frames it (`openingView`).
      // It ends without a revision, so the request is what brings that follow.
      // Not in 3D, where a landing moves neither camera nor picture (D223) and a
      // request left standing would move the flat view the reader comes back to.
      ...(ended && get().viewMode === '2d' && openedTheGraph(finishedSession, ended)
        ? { frameOpening: true }
        : {}),
    })

    // The charge, said out loud the moment it lands. One place for every verb, so
    // two paths cannot phrase the same figure two ways. The scan, the link search,
    // the prior-art search and the field sweep keep their own endings — their
    // result IS a sentence, and the charge rides inside it at the call site. Quiet during a run (the pod carries the
    // run's own figure), on a failed or refused pass (the error is the news), and
    // for any pass with no per-op row — which includes the whole demo.
    const live = get().run
    if (
      ended?.spend &&
      ended.kind !== 'link' &&
      ended.kind !== 'priorArt' &&
      ended.kind !== 'sweep' &&
      ended.kind !== 'bridge' &&
      !done?.error &&
      !done?.refused &&
      // Quiet only while the run is MOVING: those are its own passes, and the
      // pod carries their figure. A pass that ends while a run is WAITING at
      // its confirm gate is the person's own hand — it bills the same ledger
      // the run's ceiling reads, and the pod's figure re-projects only on the
      // gate's release, so this toast is the one receipt that pause gets.
      (live === null || live.phase !== 'moving')
    ) {
      const p = priceOf(ended.spend.model.id)
      if (p) {
        const delta = estimateUsd(ended.spend, p)
        const ledger = estimateSpendUsd(get().session.spend)
        const cap = Number.parseFloat(get().runCeiling)
        const capped = Number.isFinite(cap) && cap > 0
        // The words the passes with their own endings carry too (`chargeLine`).
        const { line, over } = chargeLine(delta, ledger.usd, capped ? cap : null)
        // What landed leads, and the figure follows it (D335): the end of a pass
        // is what it gave the reader, and the price alone in green was the app
        // congratulating itself for being paid — the toast Setup refuses a saved
        // key for the same reason. Counted as `landingLine` counts (what landed
        // beside the targets), in the noun it says them in (`passLanded`) — a
        // panel's dissent is an objection in both, never a claim in one. An
        // insight pass says its proposals as its own row does. Informational, or
        // the warning past the cap — never the insight ink.
        const landed = ended.produced.filter((id) => !ended.targetIds.includes(id)).length
        const what = landed ? `${passLanded(ended.kind, landed)} · ` : ''
        get().toast(`${what}${line}`, over ? 'warn' : 'info')
      }
    }
  },

  setThought: (t) => set({ thought: t }),
  // Each step joins the trail as well as standing as the line: the line over the
  // field shows the newest, the live pass's message lists the last few.
  setSearching: (t) =>
    set((s) =>
      t === null
        ? { searching: null }
        : { searching: t, searchTrail: [...s.searchTrail, t].slice(-TRAIL_KEEP) },
    ),
  setWaiting: (t) => set({ waiting: t }),

  // ── selection & camera ───────────────────────────────────────────────────

  select: (id) => {
    set({
      selectedId: id,
      rightTab: id ? 'node' : get().rightTab,
      // Selecting is the person taking the map back. The coach remains available
      // from Guide me, but it stops directing attention immediately.
      coach: calmedCoach(get().coach),
    })
  },
  hover: (id) => {
    if (get().hoverId !== id) set({ hoverId: id })
  },
  setFocus: (id) => {
    // Focus changes the candidate field the guide reads, and the coach needs no
    // reset for that — it reads the focused subtree on its next pass. So it is
    // kept, with its retirement, and only the card stands down.
    set({ focusId: id, coach: calmedCoach(get().coach) })
  },

  setView: (v, byUser = true) => {
    set(
      byUser
        ? {
            view: v,
            viewPinned: true,
            viewTarget: null,
            coach: calmedCoach(get().coach),
          }
        : { view: v },
    )
  },

  /**
   * Switch canvases.
   *
   * Entering the spatial view re-frames the camera rather than restoring wherever
   * it was left. That is deliberate: the graph has almost certainly grown since,
   * and arriving inside a volume pointed at empty space is disorienting in a way
   * that arriving at a stale 2D pan is not — you cannot tell whether you are lost
   * or the graph is gone. Leaving does not touch the flat view's camera, so going
   * back returns you to exactly the pan and zoom you had.
   */
  setViewMode: (m, w, h) => {
    if (get().viewMode === m) return
    // Going back to the flat view returns the pan and zoom it had, so no framing
    // asked before the switch may answer after it.
    if (get().frameOpening) set({ frameOpening: false })
    const coach = calmedCoach(get().coach)
    if (m === '3d') {
      const s = get().session
      let radius = 420
      for (const id of s.order) {
        const n = s.nodes[id]
        if (n) radius = Math.max(radius, Math.hypot(n.x, n.y))
      }
      const f = visibleField(w ?? 1200, h ?? 800, get().viewInset)
      set({ viewMode: m, cam: camFor(radius, w ?? 1200, f.x1 - f.x0, f.y1 - f.y0), coach })
      return
    }
    set({ viewMode: m, coach })
  },

  // Every one of these clears `camTarget`. A hand on the camera outranks an
  // easing in flight, and leaving the target set would have the two fight for the
  // rest of the flight — the drag winning each frame and the easing undoing it.
  orbitCam: (dYaw, dPitch) => {
    set({
      cam: orbit(get().cam, dYaw, dPitch),
      camTarget: null,
      coach: calmedCoach(get().coach),
    })
  },
  dollyCam: (factor) => {
    set({
      cam: dolly(get().cam, factor),
      camTarget: null,
      coach: calmedCoach(get().coach),
    })
  },
  panCam: (dx, dy, w) => {
    set({
      cam: pan(get().cam, dx, dy, w),
      camTarget: null,
      coach: calmedCoach(get().coach),
    })
  },
  raiseCam: (dy) => {
    set({
      cam: raise(get().cam, dy),
      camTarget: null,
      coach: calmedCoach(get().coach),
    })
  },

  flyCam: (c) => set({ camTarget: clampCam(c) }),
  applyCam: (c, arrived) => set(arrived ? { cam: c, camTarget: null } : { cam: c }),

  pivotOn: (id, byUser = true) => {
    const n = get().session.nodes[id]
    if (!n) return
    const c = get().cam
    get().flyCam({
      ...c,
      target: { x: n.x, y: contactHeight(n), z: n.y },
      // Close in, but never all the way: a pivot that also flew you to arm's
      // length would lose the context the move is for.
      dist: byUser ? Math.max(560, c.dist * 0.62) : c.dist,
    })
    if (byUser) get().dismissCoach()
  },

  frameCam: (w, h) => {
    const s = get().session
    let radius = 420
    for (const id of s.order) {
      const n = s.nodes[id]
      if (n) radius = Math.max(radius, Math.hypot(n.x, n.y))
    }
    // Keeps the current yaw and pitch. Someone who has found an angle they like
    // and presses Fit wants the graph framed from where they are standing, not to
    // be teleported back to the default orbit. Sized to the field the panes and
    // the composer leave, where the picture is centred — the flat Fit's field.
    const f = visibleField(w, h, get().viewInset)
    const home = camFor(radius, w, f.x1 - f.x0, f.y1 - f.y0)
    set({ camTarget: clampCam({ ...get().cam, dist: home.dist, target: home.target }) })
  },

  applyView: (v, arrived) => set(arrived ? { view: v, viewTarget: null } : { view: v }),

  unpinView: () => set({ viewPinned: false }),

  nudgeView: (dx, dy) => {
    const v = get().view
    set({
      view: { ...v, x: v.x + dx, y: v.y + dy },
      viewPinned: true,
      viewTarget: null,
      coach: calmedCoach(get().coach),
    })
  },

  zoomBy: (factor, cx, cy) => {
    const v = get().view
    const k = clamp(v.k * factor, ZOOM_MIN, ZOOM_MAX)
    if (k === v.k) return
    if (cx === undefined || cy === undefined) {
      set({
        view: { ...v, k },
        viewPinned: true,
        viewTarget: null,
        coach: calmedCoach(get().coach),
      })
      return
    }
    // Keep the world point under the cursor pinned while zooming.
    const wx = (cx - v.x) / v.k
    const wy = (cy - v.y) / v.k
    set({
      view: { k, x: cx - wx * k, y: cy - wy * k },
      viewPinned: true,
      viewTarget: null,
      coach: calmedCoach(get().coach),
    })
  },

  frame: (w, h, target) => {
    const b = target ?? get().bounds
    // The stage runs under the floating panes; the fit must not. The field is
    // what the panes and the bottom band leave visible — see `core/framing.ts`,
    // which also owns the one guard for a stage too small to honour them — less
    // the tool dock's band at its right, the 3D Fit's `FIT_EDGE` (`fitField`).
    // Unpinned: a Fit asks for the whole graph and leaves the view to follow it
    // as it grows. The palette's Fit once pinned instead, the only one of four doors
    // to Fit that did; the invariant's words now say what the other three always did.
    set({ viewTarget: fitCamera(b, w, h, get().viewInset), viewPinned: false })
  },

  /**
   * Auto-follow: keep the growing graph framed, but never zoom out past the
   * point where a node is a smudge. Beyond that the outer branches simply run
   * off the edge — which is what the minimap and Fit are for, and reads far
   * better than a legible-nothing overview.
   *
   * Refuses outright on a pinned view. This is the landing path — the loop calls
   * it on every revision, and every arriving node is one — so the guardrail
   * lives here, where no caller can forget it: a pass landing never moves a
   * view the reader has put somewhere.
   *
   * The dead zone and the headroom are `followView`'s. "The frame" is what the
   * panes and the composer leave visible: a node under glass is on the canvas
   * but not in view, and the dead zone has to know that. The selected node is
   * held inside it where the graph is too big to fit — the card in the thread
   * is about that node, and a follow that slid it under the phone's sheet would
   * leave the reader reading about something they cannot see.
   *
   * Once, after the pass that opened the graph lands, the dead zone is skipped
   * and the follow's own fit frames what landed (`frameOpening`, `openingView`):
   * the dead zone only ever zooms out, and a first ring landing into a view left
   * zoomed out stayed small. The same refusal comes first, and lowers the
   * request, so a view the reader pins before the loop answers is never moved
   * by it — then or after a Follow again.
   */
  follow: (w, h) => {
    if (w <= 0 || h <= 0) return
    const st = get()
    if (st.viewPinned) {
      if (st.frameOpening) set({ frameOpening: false })
      return
    }
    const sel = st.selectedId ? st.session.nodes[st.selectedId] : undefined
    const args = [
      st.bounds,
      st.viewTarget ?? st.view,
      visibleField(w, h, st.viewInset),
      sel ? { x: sel.x, y: sel.y } : null,
    ] as const
    if (st.frameOpening) {
      const next = openingView(...args)
      set(next ? { viewTarget: next, frameOpening: false } : { frameOpening: false })
      return
    }
    const next = followView(...args)
    if (next) set({ viewTarget: next })
  },

  centerOn: (id, w, h, k, byUser = true) => {
    const n = get().session.nodes[id]
    if (!n) return
    const kk = clamp(k ?? get().view.k, 0.12, 4)
    set({
      viewTarget: centerView(n, kk, visibleField(w, h, get().viewInset)),
      viewPinned: byUser,
      coach: byUser ? calmedCoach(get().coach) : get().coach,
    })
  },

  lookAt: (id) => {
    if (get().session.nodes[id]) set({ viewAsk: id })
  },

  settleLook: (w, h) => {
    const st = get()
    const id = st.viewAsk
    if (!id) return
    const n = st.session.nodes[id]
    if (!n || w <= 0 || h <= 0) {
      set({ viewAsk: null })
      return
    }
    if (st.viewMode === '3d') {
      // The orbit rig has no field and no pin: its projection centre is the
      // canvas centre (see `viewInset`). A node off the stage or behind the eye
      // is flown to by the pivot the double-click uses, keeping the distance —
      // the reader asked to see it, not to be flown to arm's length.
      const cam = st.camTarget ?? st.cam
      const q = makeProjector(cam, w, h).project(n.x, contactHeight(n), n.y)
      const m = LOOK_MARGIN
      set({ viewAsk: null })
      if (!q.on || q.x < m || q.x > w - m || q.y < m || q.y > h - m) st.pivotOn(id, false)
      return
    }
    // Judged against where the camera is GOING, not where it is: a palette
    // press centres the node itself a moment before the loop answers this, and
    // a fit or a follow already in flight is the view the reader will see.
    const next = lookView(n, st.viewTarget ?? st.view, visibleField(w, h, st.viewInset))
    if (!next) {
      set({ viewAsk: null })
      return
    }
    // Pinned, as a pan is: the reader has said where they want to look, and
    // the next arriving node must not answer by re-framing the whole graph
    // away from it. `f` and Fit release it. The coach is left alone — the
    // `select` beside every pick already stood it down, and a cue's own press
    // that reveals its anchor must not close the card it came from.
    set({ viewAsk: null, viewTarget: next, viewPinned: true })
  },

  // ── settings ─────────────────────────────────────────────────────────────

  toggleFilter: (k) => {
    const f = get().filters
    set({
      filters: { ...f, [k]: !f[k] },
      coach: calmedCoach(get().coach),
    })
  },

  // One `set`, so the coach is calmed once and the canvas re-derives once,
  // rather than seven toggles each doing both.
  showAllChannels: () => {
    const f = get().filters
    const next = withChannelsOn(f)
    if (next === f) return
    set({ filters: next, coach: calmedCoach(get().coach) })
  },

  // The coach card's "Show it". Not calmed: the press is the card's own, and the
  // card has to stay to stand beside the node it just brought back — the rule
  // `settleLook` keeps for a cue's reveal. Through `revealNode`, so the camera
  // half is a request, and a reader's pick, as every other jump by name is.
  showNode: (id) => {
    const f = get().filters
    const next = filtersShowing(get().session, f, id)
    if (next !== f) set({ filters: next })
    get().revealNode(id)
  },

  setTheme: (t) => {
    document.documentElement.setAttribute('data-theme', t)
    writeLS(LS.theme, t)
    set({ theme: t })
  },

  setLens: (l) => set({ lens: l }),
  setTemper: (t) => set({ temper: t }),

  setEffortLevel: (work, level) => {
    // `null` CLEARS rather than pinning the default, for `setRoleModel`'s reason:
    // cleared follows the default, and set stays where it was put. A level off
    // the ladder cannot arrive from the rows, which render the ladder; the guard
    // is for a caller that did not. Synchronous, like the model, because the next
    // pass reads it — and the whole map is written, `{}` included, so that the
    // profile a pre-D313 workspace chose is never read again once a level is set.
    const next = { ...get().effortLevels }
    if (level && isEffort(level)) next[work] = level
    else delete next[work]
    writeLS(LS.effortLevels, JSON.stringify(next))
    writeLS(LS.effort, null)
    set({ effortLevels: next })
  },

  setModel: (id) => {
    // Written synchronously rather than through the prefs autosave: a debounced
    // write that never fires means the next boot reasons with a different model
    // than the one the user chose, and the only visible symptom is a capability
    // note that no longer matches.
    const next = findModel(id) ? id : DEFAULT_MODEL_ID
    writeLS(LS.model, next)
    set({ modelId: next })
  },

  setRoleModel: (role, id) => {
    // `null` CLEARS rather than pinning the default, and the two are different
    // states worth keeping apart: cleared means "wherever the selection goes",
    // and follows it when the selection changes; pinned to the same id means
    // "here specifically", and does not. A picker that could only ever pin would
    // make the first state unreachable once you had left it.
    const next = { ...get().roleModels }
    if (id && findModel(id)) next[role] = id
    else delete next[role]
    // Written synchronously, like the selection and for the same reason — see
    // `setModel`. An empty map is stored as `{}` rather than removed, so that a
    // user who deliberately cleared every role is distinguishable on the next
    // boot from one who never routed anything. Nothing branches on it today; it
    // costs two bytes and keeps the option.
    writeLS(LS.roles, JSON.stringify(next))
    set({ roleModels: next })
  },

  setKey: (p, k) => {
    const v = k && k.trim() ? k.trim() : null
    writeLS(LS.key(p), v)
    set({ keys: { ...get().keys, [p]: v } })
  },

  setCustom: (base, model, dialect) => {
    const b = base.trim()
    const m = model.trim()
    const d = dialect ?? get().customDialect
    writeLS(LS.customBase, b || null)
    writeLS(LS.customModel, m || null)
    // Absent means Responses, so only the departure is written.
    writeLS(LS.customDialect, d === 'chat' ? 'chat' : null)
    set({ customBase: b, customModel: m, customDialect: d })
  },

  setTuneDefaults: (t) => {
    // Synchronous, like the selection and the keys: a debounced write that never
    // fired would leave the next exploration starting from counts nobody chose,
    // and the counts are what a pass does and what it draws down.
    writeLS(LS.tune, JSON.stringify(t))
    set({ tuneDefaults: t })
  },

  setSourceOn: (id, on) => {
    // Both states are recorded, on and off. A row turned OFF has to survive as a
    // decision rather than as an absence, because an absence is what gives the row
    // back its registry default on the next boot — which would silently re-enable
    // whatever was just turned off.
    const next = { ...get().sourcesOn, [id]: on }
    writeLS(LS.sourcesOn, JSON.stringify(next))
    set({ sourcesOn: next })
  },

  setSourceMailto: (v) => {
    // Trimmed like the custom endpoint, and removed rather than stored empty: an
    // empty string in the slot and no slot at all mean the same thing to every
    // caller, and only one of them survives a reload as itself.
    const next = v.trim()
    writeLS(LS.sourceMailto, next || null)
    set({ sourceMailto: next })
  },

  setProfileName: (v) => {
    // Trimmed and removed rather than stored empty, exactly like the contact
    // address above: an empty string in the slot and no slot at all mean the
    // same thing to every reader, and only one of them survives a reload as
    // itself. Written synchronously — this is a name, not a preference, and a
    // debounced write that never fired would lose it without saying so.
    const next = v.trim()
    writeLS(LS.profileName, next || null)
    set({ profileName: next })
  },

  declareProject: (raw) => {
    const label = projectLabel(raw)
    if (!label) return null
    const next = withDeclared(get().declaredProjects, label)
    // Written synchronously beside the keys, for the reason the table gives: a
    // project somebody names and then reloads on top of must still be there.
    writeLS(LS.projects, serialiseDeclared(next))
    set({ declaredProjects: next })
    return label
  },

  undeclareProject: (label) => {
    const next = withoutDeclared(get().declaredProjects, label)
    if (!declaredChanged(get().declaredProjects, next)) return
    writeLS(LS.projects, serialiseDeclared(next))
    set({ declaredProjects: next })
  },

  syncProjects: (inUse) => {
    const cur = get().declaredProjects
    const next = declaredAfter(cur, inUse)
    // Silent when nothing moved, and that is load-bearing rather than tidy: the
    // caller is a render effect, so a set() on every pass would re-render the
    // pane every time it re-read the index, which is every save.
    if (!declaredChanged(cur, next)) return
    writeLS(LS.projects, serialiseDeclared(next))
    set({ declaredProjects: next })
  },

  // One pane left since the library rail retired — the side argument went with it.
  setRail: (open) => {
    set({
      railRight: open,
      // Closing the surface is an explicit Skip. Without this, the director opens
      // it again on its next effect and the inspector becomes impossible to
      // dismiss until every cue has been advanced through. That Skip and no more
      // than that: closing the pane says nothing about any one cue, so the skip
      // arithmetic stays with `retireCue`.
      coach: !open ? calmedCoach(get().coach) : get().coach,
    })
  },
  // Persisted through the prefs autosave rather than written here: unlike the
  // profile name this is taste, and the worst a lost write does is open a panel
  // the next press closes again. `prefsChanged` has to name it — see autosave.ts.
  setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
  setWeighting: (w) => set({ weighting: w }),
  setInstruments: (v) => set({ instruments: v }),
  // The footer's switches, persisted like the two above (`prefsChanged` names
  // all three).
  setShowRings: (v) => set({ showRings: v }),
  setDimUnrelated: (v) => set({ dimUnrelated: v }),
  setShowMinimap: (v) => set({ showMinimap: v }),
  /* Anything that is not a finite number is NO SETTING, and NaN is the one that
     had to be named. It is never equal to itself, so a NaN in the slot makes
     `prefsChanged` answer true on every state change for the rest of the
     session — the blob rewritten on every debounce, for a value `JSON.stringify`
     then turns into null anyway. A guard in the setter is the one place it can
     be caught, because this is the only door. */
  setThreadWidth: (w) =>
    set({ threadWidth: typeof w === 'number' && Number.isFinite(w) ? Math.round(w) : null }),
  // The same guard, for the same NaN, on the other pane's number.
  setSidebarWidth: (w) =>
    set({ sidebarWidth: typeof w === 'number' && Number.isFinite(w) ? Math.round(w) : null }),
  // A measurement the pane publishes; the same NaN guard, because a NaN here
  // would make every bound it feeds answer with the floor.
  setSidebarFit: (px) =>
    set({ sidebarFit: typeof px === 'number' && Number.isFinite(px) ? Math.round(px) : null }),
  // Taste, like the sidebar's own collapse: persisted through the prefs
  // autosave, and `prefsChanged` names it — see autosave.ts.
  toggleProjectFold: (label) => {
    const next = toggleFolded(get().foldedProjects, label)
    if (next) set({ foldedProjects: next })
  },
  // Taste as well, persisted the same way and named in `prefsChanged` too.
  markOpened: (id) => {
    const next = withOpened(get().openedAt, id, Date.now())
    if (next) set({ openedAt: next })
  },
  // A tab the reader presses is theirs from then on, a copy's pane included.
  setRightTab: (t) => set({ rightTab: t, readerTab: null }),
  setPalette: (open) => set({ paletteOpen: open }),
  setSetup: (mode, section) => {
    // The ONE writer of `splashSeen`, and it fires on the way out of splash mode
    // however that happens — Escape, the backdrop, "look around", or Save. A
    // second writer is how a first screen comes back on the second visit.
    const was = get().setupOpen
    set(
      was === 'splash' && mode === null
        ? { setupOpen: null, splashSeen: true, keyAsk: null }
        : mode === 'settings'
          ? // The section is written only on the way IN to settings, and always —
            // an omitted argument means the providers rows, never "wherever it was
            // left". Every existing caller passes nothing and lands where it did.
            { setupOpen: mode, setupSection: section ?? 'providers' }
          : // Closing forgets which key was asked for: the next open is ordinary.
            { setupOpen: mode, ...(mode === null ? { keyAsk: null } : {}) },
    )
    // The growth animation is the argument for this tool, and the splash's
    // ~600 words always outlast the demo's ~21 seconds — so by dismissal the
    // unfurl had already happened behind the sheet's blur, unseen. Replay it:
    // the same start as the dock's play button, and only when the pristine
    // demo has finished growing (mid-unfurl it is already playing in full
    // view, and a live run must never be scrubbed out from under itself).
    if (was === 'splash' && mode === null) {
      const s = get()
      if (
        s.session.id === DEMO_SESSION_ID &&
        s.session.order.length > 1 &&
        !s.busy &&
        s.run === null &&
        s.playhead === null &&
        !s.playing
      ) {
        s.setPlayhead(1)
        s.setFocus(null)
        s.setPlaying(true)
      }
    }
  },
  askForKey: (provider, ...ask) => {
    get().setSetup('settings', 'providers')
    const shown = keyCardProviders(JUDGE_ENDPOINT).includes(provider)
    set({
      keyAsk: !shown
        ? null
        : ask[0] === 'missing'
          ? { provider, why: 'missing', need: ask[1] }
          : { provider, why: 'rejected' },
    })
  },
  askForMissingKey: (role, what) => {
    const { provider, need } = missingKeyNeed(get(), role, what)
    get().askForKey(provider, 'missing', need)
  },
  askForOtherKey: (besides, need) => {
    const s = get()
    const other = keyCardProviders(JUDGE_ENDPOINT).find(
      (p) => !besides.includes(p) && !PROVIDERS[p].judge && !PROVIDERS[p].custom && !s.keys[p],
    )
    if (other) return s.askForKey(other, 'missing', need)
    s.setSetup('settings', 'providers')
    set({ keyAsk: null })
  },
  setSources: (open) => set({ sourcesOpen: open }),
  setResearchOpen: (open, tab) =>
    set((state) => ({ researchOpen: open, researchTab: tab ?? state.researchTab })),
  setResearchState: (research) => {
    const state = get()
    if (research.assessments !== undefined && !parseRunAssessments(research.assessments)) {
      state.toast(
        'The assessment ledger is invalid. The existing research record was preserved.',
        'warn',
      )
      return
    }
    const normalized = pruneManualEvidenceBindingsForNodes(
      normalizeResearchState(research),
      new Set(state.session.order.filter((nodeId) => !!state.session.nodes[nodeId])),
    )
    const session = withKnowledgeRevision(
      { ...state.session, research: normalized },
      'Research record updated',
    )
    set({
      session,
      rev: state.rev + 1,
      coach: calmedCoach(state.coach),
    })
  },
  setRunAssessments: (workspace) => {
    const parsed = parseRunAssessments(workspace)
    if (!parsed) return false
    const state = get()
    const research = state.session.research ?? createResearchState({ now: state.session.createdAt })
    set({
      session: { ...state.session, research: { ...research, assessments: parsed } },
      rev: state.rev + 1,
    })
    return true
  },
  setExperimentWorkspace: (workspace) => {
    const parsed = parseExperimentWorkspace(workspace)
    if (!parsed) {
      get().toast('The experiment record is malformed or exceeds its storage limits.', 'warn')
      return false
    }
    set((state) => {
      const session = {
        ...state.session,
        research: {
          ...(state.session.research ?? createResearchState({ now: state.session.createdAt })),
          experiments: parsed,
        },
      }
      if (!session.order.length) {
        // Durable explorations have a seed. Research Lab is reachable before a
        // model call, so retain the declared question as an unpriced seed in
        // THIS session. startSession would change the contract's context ID.
        const root = makeNode({
          label: parsed.experiments[0]?.title ?? 'Executable research workspace',
          claim:
            parsed.experiments[0]?.question ??
            'Prepare and run a bounded local research experiment.',
          kind: 'seed',
          origin: 'seed',
          confidence: 0.5,
          novelty: 0.5,
          tension: 0,
          createdOrdinal: 0,
        })
        session.seed = root.claim
        session.nodes = { [root.id]: root }
        session.order = [root.id]
        session.nextNodeOrdinal = 1
        return {
          session,
          rev: state.rev + 1,
          selectedId: root.id,
          bounds: layout(session.nodes, session.order),
        }
      }
      return { session, rev: state.rev + 1 }
    })
    return true
  },
  setComputationalWorkspace: (workspace) => {
    const parsed = parseComputationalWorkspace(workspace)
    if (!parsed) {
      get().toast(
        'The calculation draft exceeds its storage limits or has invalid metadata.',
        'warn',
      )
      return false
    }
    set((state) => ({
      session: {
        ...state.session,
        research: {
          ...(state.session.research ?? createResearchState({ now: state.session.createdAt })),
          computationalWorkspace: parsed,
        },
      },
      rev: state.rev + 1,
    }))
    return true
  },
  applySemanticSnapshot: (snapshot) => {
    const state = get()
    state.mark(OP_LABEL.research)
    const session = materialiseSemanticSnapshot(state.session, snapshot)
    set({
      session,
      bounds: layout(session.nodes, session.order),
      selectedId: state.selectedId && session.nodes[state.selectedId] ? state.selectedId : null,
      focusId: state.focusId && session.nodes[state.focusId] ? state.focusId : null,
      playhead: null,
      playing: false,
      rev: state.rev + 1,
      coach: null,
    })
  },
  setLibrary: (open) => set({ libraryOpen: open }),
  setSearchOpen: (open) => set({ searchOpen: open }),
  // Closing clears the target unconditionally, so the sheet's next opener cannot
  // inherit a document or a landing row it never named — the plain
  // `setExport(true)` callers mean "the open exploration, from the top", and
  // that has to be what they get.
  setExport: (open, sessionId, section) =>
    set({
      exportOpen: open,
      exportSessionId: open ? (sessionId ?? null) : null,
      exportSection: open ? (section ?? null) : null,
    }),
  setShortcuts: (open) => set({ shortcutsOpen: open }),
  setPromptOpen: (open) => set({ promptOpen: open }),
  setPreferCorpus: (v) => set({ preferCorpus: v }),
  proposeSeed: (seed) => set({ proposedSeed: seed }),
  proposeQuestion: (q) => set({ proposedQuestion: q }),

  setPlayhead: (i) => {
    set({
      playhead: i,
      playheadAt: Date.now(),
      coach: calmedCoach(get().coach),
    })
  },
  setPlaying: (v) => {
    set({
      playing: v,
      coach: calmedCoach(get().coach),
    })
  },

  setTempo: (t) => set({ tempo: clamp(t, 0.2, 6) }),

  // ── toasts ───────────────────────────────────────────────────────────────

  toast: (text, tone = 'info', action) => {
    const t: Toast = { id: uid('t'), text, tone, action }
    // Three kept, not two: an actionable toast that REFUSES pushes its
    // explanation as a fourth, and at the tighter cap that eviction fell on the
    // toast being explained — the offer disappeared and only the reason for it
    // being declined remained, which is the wrong half to keep.
    set({ toasts: [...get().toasts.slice(-3), t] })
    // An actionable toast lingers like a 'bad' one: it is offering a tap, and
    // 3.8 seconds is a read window, not a decide-and-reach window.
    setTimeout(() => get().dismissToast(t.id), tone === 'bad' || action ? 6500 : 3800)
  },

  dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}))

/* ── which model, and whether it can run ──────────────────────────────────
   Three pure reads over two fields, so the engine can ask imperatively and the
   UI reactively and get one answer to one question. Two copies of "which model"
   is how a topbar comes to name one model while the transport calls another. */

/** The selected model's entry, falling back to the default so this can never
 *  return undefined. */
export function activeEntry(s: Pick<State, 'modelId'>): ModelEntry {
  return findModel(s.modelId) ?? findModel(DEFAULT_MODEL_ID) ?? MODELS_FALLBACK
}
// Belt and braces for a hand-edited registry: `activeEntry` is called on the
// frame path and in `beginOp`, and neither can cope with undefined.
const MODELS_FALLBACK: ModelEntry = {
  id: DEFAULT_MODEL_ID,
  provider: 'anthropic',
  name: DEFAULT_MODEL_ID,
  note: '',
  search: 'results',
  efforts: ['high'],
}

/**
 * The model the next pass will run on, or null when the selected model's
 * provider has no key.
 *
 * This replaces every `!s.apiKey` guard. With two providers "no key" is not a
 * fact about the app, it is a fact about the SELECTED MODEL — and a user holding
 * an OpenAI key with Claude selected must be told which key is missing rather
 * than silently handed the demo.
 */
/**
 * The state a resolution reads. `customDialect` is optional here and required on
 * the store: absent means Responses, which is what every caller that predates
 * the field means by leaving it out.
 */
export type ResolveView = Pick<State, 'modelId' | 'keys' | 'customBase' | 'customModel'> & {
  customDialect?: State['customDialect']
}

export function resolveModel(s: ResolveView): ResolvedModel | null {
  const entry = activeEntry(s)
  if (PROVIDERS[entry.provider].custom) {
    // An address, not a key. A model running on this machine needs no credential,
    // so requiring one would lock out the one case that needs no account at all —
    // while an address is the thing without which there is nothing to call.
    // An address the settings form would refuse counts as none, as the judge's
    // does (`judgeEndpoint`): the form is not the only writer — a value saved by
    // an older build, another tab or a hand edit reaches here unchecked, and a
    // plaintext one would carry the prompt and key across the network in the
    // clear (D335). It then refuses exactly as a missing address does.
    if (!s.customBase.trim() || baseUrlProblem(s.customBase)) return null
    return {
      // The user's own model name replaces the row's placeholder id, so the stamp
      // on every node names what actually answered rather than "custom-endpoint".
      entry: s.customModel.trim()
        ? { ...entry, id: s.customModel.trim(), name: s.customModel.trim() }
        : entry,
      apiKey: s.keys.custom ?? '',
      baseURL: s.customBase.trim(),
      // Only the departure is carried, so a resolved Responses endpoint looks
      // exactly as it did before the field existed.
      ...(s.customDialect === 'chat' ? { dialect: 'chat' as const } : {}),
    }
  }
  const apiKey = s.keys[entry.provider]
  return apiKey ? { entry, apiKey } : null
}

/** The state a routing question needs. Named once so the six functions below
 *  cannot drift apart on what they read. */
export type RoutingState = ResolveView & Pick<State, 'roleModels'>

/** Just the two fields that decide WHERE a role goes, exported because `run.ts`
 *  asks the same question and `State` itself is deliberately module-private. */
export type RouteView = Pick<State, 'modelId' | 'roleModels'>

/**
 * The model id a role would actually use — its override, or the selection.
 *
 * The one place the sparse map is read, so "absent means the selection" is
 * written down once rather than assumed in nine call sites. An override naming a
 * model this build does not know counts as absent, which is what `bootRoles`
 * makes of one on the way in. Resolving it through `activeEntry` instead would
 * land on `DEFAULT_MODEL_ID`, and that only coincided with the selection while
 * nobody had moved either.
 */
export const roleModelId = (s: Pick<State, 'modelId' | 'roleModels'>, role: Role): string => {
  const routed = s.roleModels[role]
  return routed && findModel(routed) ? routed : s.modelId
}

/** The registry entry a role would use. Falls back exactly as `activeEntry` does,
 *  because it IS `activeEntry` with a different id. */
export const entryFor = (s: Pick<State, 'modelId' | 'roleModels'>, role: Role): ModelEntry =>
  activeEntry({ modelId: roleModelId(s, role) })

/**
 * The model a pass of this kind will run on, or null when it has no key.
 *
 * Resolved through `resolveModel` rather than beside it — the same reason
 * `resolveAuditor` does it: there is one definition of "callable" and a second
 * one would drift out of step with the custom endpoint's rules within a release.
 *
 * There is deliberately NO fallback to the selection when a routed model has no
 * key. Falling back would send a source check to a model the user pointed
 * somewhere else, quietly, and the symptom would be a check that returns nothing
 * on a model that cannot search — a wrong answer dressed as an empty one. Null
 * instead, and the caller names the role and the key that is missing.
 */
export function resolveFor(s: RoutingState, role: Role): ResolvedModel | null {
  return resolveModel({ ...s, modelId: roleModelId(s, role) })
}

/**
 * Every distinct model a run could bill, selection included.
 *
 * The money path's input. A run under a ceiling has to reserve against the
 * dearest pass it could make, and with routing that is no longer necessarily the
 * selected model — so anything sizing a reserve asks this rather than reading
 * `activeEntry`. Deduplicated by id, because the common case is one model in
 * every slot and a reserve counted five times is not a reserve, it is a refusal.
 */
export function routedEntries(s: Pick<State, 'modelId' | 'roleModels'>): ModelEntry[] {
  const seen = new Map<string, ModelEntry>()
  seen.set(activeEntry(s).id, activeEntry(s))
  for (const role of ROLES) {
    const e = entryFor(s, role)
    if (!seen.has(e.id)) seen.set(e.id, e)
  }
  return [...seen.values()]
}

/**
 * A model from a DIFFERENT house than the one given, for the cross-examination.
 *
 * Returns null rather than falling back, and the refusal is the feature: an
 * audit run by the same provider that wrote the claim is the panel again, with
 * its admitted ceiling intact and a label on it implying otherwise. Better to
 * disable the button and say why.
 *
 * A custom endpoint counts as its own house and is eligible — what is behind
 * that address is between the user and whoever runs it, which is the app's
 * standing position everywhere else. It is taken last, because a proxy in front
 * of the very model being audited would defeat the whole pass and only the user
 * can know whether that is the case.
 */
export function resolveAuditor(s: ResolveView, against: ProviderId): ResolvedModel | null {
  const candidates = MODELS.filter((m) => m.provider !== against)
  const order = [...candidates].sort(
    (a, b) => Number(PROVIDERS[a.provider].custom) - Number(PROVIDERS[b.provider].custom),
  )
  for (const entry of order) {
    // Resolved through the same function the app uses everywhere, so a custom
    // endpoint still needs its address and a hosted model still needs its key —
    // there is no second definition of "callable" to drift out of step.
    const r = resolveModel({ ...s, modelId: entry.id })
    if (r) return r
  }
  return null
}

/**
 * The other house to the one REASONING runs on — the auditor a run resolves
 * before it spends (`startRun`), the one its key gap asks for (`runKeyGap`),
 * and the one the composer prices a run's cross-examinations at. A run's claims
 * are written on the reasoning route, so that is the house an audit of them has
 * to leave. The composer read the selection's house instead, so with reasoning
 * routed to another company it projected a run's audits at the rate of the
 * model the run would audit WITH, and refused or allowed them on that reading.
 */
export const runAuditor = (s: RoutingState): ResolvedModel | null =>
  resolveAuditor(s, entryFor(s, 'reason').provider)

/** Whether a paid pass can run at all. Never `keys.anthropic !== null` — a key
 *  for the wrong provider is indistinguishable from no key, and treating it as
 *  one is how an Anthropic secret reaches api.openai.com. */
export function canCall(s: ResolveView): boolean {
  return resolveModel(s) !== null
}

/**
 * Whether a source check on a claim, routed as things stand, has anything to
 * read: a model that searches, or the person's own documents under “Prefer my
 * sources”.
 *
 * `runVerify` refuses without one of the two — a check with neither would come
 * back empty and read as “nothing found”, the one confusion the check exists to
 * prevent — and it refuses before `beginOp`, saying nothing when a run asked it
 * to be quiet. So a run asks this first, and has to ask exactly this: a loop
 * whose question drifted from the verb's refusal would propose a check the verb
 * refuses, lap after lap. One definition, read by both (D293).
 *
 * The seed is not a claim: V on it is a survey of the field, which reads only
 * the web and refuses on its own terms in `runSurvey`. No run asks for one —
 * `checkableClaim`, `restingEligible` and `agendaStepIssue` all leave the seed
 * out — so this never has to answer for it.
 */
export function checkCanRead(s: Pick<State, 'modelId' | 'roleModels' | 'preferCorpus'>): boolean {
  return entryFor(s, 'check').search !== 'none' || s.preferCorpus
}

/**
 * Whether a pass holds the lock and will not yield it: `busy`, less the worked
 * example's unfurl, which the doors that replace or extend the graph stop rather
 * than wait for (`yieldUnpaid`). Read by the controls in front of those doors, so
 * they offer themselves while the demo unfolds instead of greying out for it.
 */
export function lockHeld(s: Pick<State, 'busy' | 'interrupt'>): boolean {
  return s.busy && s.interrupt === null
}

/**
 * The provider a role's pass would actually go to, for the sentence that names a
 * missing key.
 *
 * Routing makes "add your Anthropic key" a claim that can be wrong: a user whose
 * reasoning is on Claude and whose source check is routed to a GPT‑5.6 tier is
 * missing an OpenAI key for that one feature, and being sent to paste an
 * Anthropic key they already have is the least useful sentence the app could
 * produce. Every refusal below names this instead of `activeProvider`.
 */
export const providerFor = (s: Pick<State, 'modelId' | 'roleModels'>, role: Role) =>
  PROVIDERS[entryFor(s, role).provider]

/**
 * What needed the key, for a refusal that names nothing more exact: the card
 * reads "The source check runs on <model> — it needs an OpenAI key." The
 * Routing tab's rows (`ROLE_LABEL`), where the reader could move the role
 * instead, put in the singular so that each reads with "runs on".
 */
const ROLE_NEED: Record<Role, string> = {
  reason: 'Reasoning',
  check: 'The source check',
  panel: 'A panel',
  scan: 'Finding connections',
  chat: 'Asking',
}

/**
 * Whose key a pass of this role would ask for, and what the card would say needed
 * it — the house and the `KeyNeed` `askForMissingKey` opens the sheet on. Read
 * before the press as well, by a note that says what is missing where the press
 * sits (the check's, the panel's), so the note and the card the press opens are
 * one sentence about one route (`entryFor`), never the selection's.
 */
export function missingKeyNeed(
  s: Pick<State, 'modelId' | 'roleModels'>,
  role: Role,
  what?: string,
): { provider: ProviderId; need: KeyNeed } {
  const e = entryFor(s, role)
  return { provider: e.provider, need: { what: what ?? ROLE_NEED[role], on: e.name } }
}

/**
 * Every house this browser can currently call, in registry order.
 *
 * The model chip names the SELECTED model, which is the right thing for it to
 * name — it is what a pass would actually reason with. But a second provider's
 * key is a capability rather than an inventory detail: it is the entire
 * precondition for a cross-examination, and until this existed a user who
 * pasted one saw the chip change to the new model and had no way to tell that
 * the first key was still there. It reads as having replaced it.
 *
 * Derived from `PROVIDERS` rather than listing the three by hand, for the reason
 * `PROVIDER_OK` in the codec learned the hard way: a hand-written copy of the
 * provider list is a thing a fourth provider silently falls out of.
 */
export function connectedProviders(s: Pick<State, 'keys' | 'customBase'>): ProviderId[] {
  return (Object.keys(PROVIDERS) as ProviderId[]).filter(
    (p) =>
      // A judge is not a house this browser can REASON with, which is the
      // question this list answers; it has its own resolver below.
      !PROVIDERS[p].judge &&
      // A custom endpoint is an address, not a credential — the same rule
      // `resolveModel` applies, because a local model needs no key at all.
      (PROVIDERS[p].custom ? !!s.customBase.trim() : !!s.keys[p]),
  )
}

/**
 * The judge a pass may ask, or null — which is every default build, and a build
 * that names an endpoint until a TypeSafe key is stored.
 *
 * Null turns nothing off and falls back to nothing, and both halves matter. A
 * routed ROLE with no key refuses (invariant 4) because a pass quietly served by
 * another model would be a wrong answer dressed as an empty one. A judge is not
 * a role: it produces no node, no reading and no citation, so its absence is the
 * app's ordinary state — the run orders its checks by structure alone, the check
 * records no second reading — and every surface that would have printed its
 * answer says instead that it was not asked. What would violate invariant 4 is
 * the reasoning model being asked the judge's question in its place, and no
 * caller of this function has a path to do that.
 *
 * The endpoint is checked before the key because the page has no other way to
 * the host: api.typesafe.ai refuses a browser's preflight, so a key with nowhere
 * to go would be a judge that fails every call, with retries, on every check
 * wave and every source check — see `JUDGE_ENDPOINT` and D107. The parameter
 * exists so both branches are testable without stubbing the build; every caller
 * in the product takes the default.
 */
export function resolveJudge(
  s: Pick<State, 'keys'>,
  endpoint: string | null = JUDGE_ENDPOINT,
): ResolvedJudge | null {
  const apiKey = s.keys.typesafe
  const entry = findJudge(DEFAULT_JUDGE_ID)
  return endpoint && apiKey && entry ? { entry, apiKey, baseURL: endpoint } : null
}

/** The provider record behind the selection, for copy that names a host. */
export function activeProvider(s: Pick<State, 'modelId'>) {
  return PROVIDERS[activeEntry(s).provider]
}

/**
 * The host a request actually goes to, for the sentences that name one.
 *
 * A custom endpoint's host is whatever the user typed, so a surface that read
 * `provider.host` would print an empty string where the destination belongs — in
 * the one case where the destination is the thing the user most needs to see.
 */
export function activeHost(s: Pick<State, 'modelId' | 'customBase'>): string {
  const p = PROVIDERS[activeEntry(s).provider]
  return p.custom ? hostOfBase(s.customBase) : p.host
}

/* Hooks, so six components do not hand-write the same selector. Both subscribe
   to the two fields the answer depends on and nothing else. */
export const useActiveEntry = (): ModelEntry => useStore((s) => activeEntry(s))
export const useCanCall = (): boolean => useStore((s) => canCall(s))
/** Whether an Ask can be sent: the chat role's key, a routed one where routed —
 *  not the selection's, which a routed Ask never uses (invariant 4). */
export const useCanAsk = (): boolean => useStore((s) => resolveFor(s, 'chat') !== null)

/** Children index, rebuilt on demand. Cheap at these graph sizes. */
export function childrenIndex(s: Session): Map<string, string[]> {
  const m = new Map<string, string[]>()
  for (const id of s.order) {
    const p = s.nodes[id]?.parentId
    if (!p) continue
    const l = m.get(p)
    if (l) l.push(id)
    else m.set(p, [id])
  }
  return m
}

/** Root → node path, for the inspector breadcrumb and prompt context. */
export function lineage(s: Session, id: string): ReasonNode[] {
  const out: ReasonNode[] = []
  const seen = new Set<string>()
  let cur: string | null = id
  while (cur && !seen.has(cur)) {
    seen.add(cur)
    const n: ReasonNode | undefined = s.nodes[cur]
    if (!n) break
    out.unshift(n)
    cur = n.parentId
  }
  return out
}

/**
 * The op whose call produced this node.
 *
 * Deliberately does not skip the root: the demo's opening op records the root in
 * `produced`, and that is how the demo teaches this affordance on a first load
 * with no key.
 */
export function producingOp(s: Session, nodeId: string): Op | undefined {
  for (let i = s.ops.length - 1; i >= 0; i--) {
    const o = s.ops[i]!
    if (o.produced.includes(nodeId)) return o
  }
  return undefined
}

/**
 * The most recent op of a given kind that ran ON a node.
 *
 * `producingOp` searches `produced`, so it can never find a check: a source
 * check and a panel produce no nodes at all. Without this the inspector's "how
 * this was checked" disclosure would have nothing to read, and the reasoning
 * behind the most expensive pass in the app would be unreachable.
 */
export function latestOpOn(s: Session, nodeId: string, kind: OpKind): Op | undefined {
  for (let i = s.ops.length - 1; i >= 0; i--) {
    const o = s.ops[i]!
    if (o.kind === kind && o.targetIds.includes(nodeId)) return o
  }
  return undefined
}

/** Every node carrying a recorded result, newest first. */
export function outcomeNodes(s: Session): ReasonNode[] {
  return s.order
    .map((id) => s.nodes[id])
    .filter((n): n is ReasonNode => !!n?.outcome)
    .sort((a, b) => b.outcome!.recordedAt - a.outcome!.recordedAt)
}

/**
 * How many descendants of a node were reasoned BEFORE its result came in.
 *
 * Compared against `recordedAt`, not `at`: `at` is when the observation happened
 * in the world, which can predate the whole exploration. What matters for a piece
 * of reasoning is whether the person writing it knew the result yet.
 */
export function staleBelow(s: Session, nodeId: string): number {
  const o = s.nodes[nodeId]?.outcome
  if (!o) return 0
  const kids = childrenIndex(s)
  let n = 0
  const stack = [...(kids.get(nodeId) ?? [])]
  const seen = new Set<string>()
  while (stack.length) {
    const id = stack.pop()!
    if (seen.has(id)) continue
    seen.add(id)
    const node = s.nodes[id]
    if (!node) continue
    // Dated by its words (`writtenAt`): a claim rewritten after the result was
    // written knowing it, as the taint walk and the canvas read it.
    if (writtenAt(node) < o.recordedAt) n++
    for (const c of kids.get(id) ?? []) stack.push(c)
  }
  return n
}

/** Whether this node sits under a result that postdates it — the reason a node
 *  can be stale without carrying an outcome of its own. */
export function staleUnder(s: Session, nodeId: string): ReasonNode | null {
  const node = s.nodes[nodeId]
  if (!node) return null
  let up = node.parentId
  const guard = new Set<string>()
  while (up && !guard.has(up)) {
    guard.add(up)
    const p = s.nodes[up]
    if (!p) break
    if (p.outcome && writtenAt(node) < p.outcome.recordedAt) return p
    up = p.parentId
  }
  return null
}
